import { useState, useEffect, useCallback } from "react";
import { getLocale, useLanguage } from "../lang/useLanguage";
import SuperAdminEdgeManager from "./SuperAdminEdgeManager";

const API = `${window.location.protocol}//${window.location.hostname}/api`;

function headers() {
  const token =
    localStorage.getItem("token") ||
    localStorage.getItem("access_token") ||
    localStorage.getItem("authToken") ||
    localStorage.getItem("opement_token");

  return {
    Authorization: `Bearer ${token || ""}`,
    "Content-Type": "application/json"
  };
}

// Commercial display names.
// IMPORTANT: backend/database plan codes remain unchanged for compatibility.
// essentials -> Essentials
// professional -> Professional
// advanced -> Advanced
// enterprise -> Enterprise
const PLAN_LABELS = {
  essentials: "Essentials",
  professional: "Professional",
  advanced: "Advanced",
  enterprise: "Enterprise"
};
const PLAN_COLORS = { trial: "#f59e0b", essentials: "#38bdf8", professional: "#3b82f6", advanced: "#8b5cf6", enterprise: "#10b981" };

const MENU_ITEMS = [
  { key: "dashboard", icon: "📊" },
  { key: "tenants", icon: "🏭" },
  { key: "users", icon: "👤" },
  { key: "licenses", icon: "🧾" },
  { key: "payments", icon: "💳" },
  { key: "crm", icon: "🤝" },
  { key: "health", icon: "🖥️" },
  { key: "backups", icon: "💾" },
  { key: "edge", icon: "🧠" },
];

export default function SuperAdmin() {
  const { t } = useLanguage();
  const sa = t.superAdmin;

  const [menu, setMenu] = useState("dashboard");
  const [tenants, setTenants] = useState([]);
  const [users, setUsers] = useState([]);
  const [userTenantFilter, setUserTenantFilter] = useState("all");
  const [loading, setLoading] = useState(false);
  const [modal, setModal] = useState(null);
  const [form, setForm] = useState({});
  const [msg, setMsg] = useState(null);
  const [backupSummary, setBackupSummary] = useState(null);
  const [backupRunning, setBackupRunning] = useState(false);
  const [healthSummary, setHealthSummary] = useState(null);
  const [plans, setPlans] = useState([]);
  const [subscriptions, setSubscriptions] = useState([]);
  const [erpConnectors, setErpConnectors] = useState({});
  const [machinePriceCatalog, setMachinePriceCatalog] = useState([]);
  const [machinePriceLoading, setMachinePriceLoading] = useState(false);

  const formatBytes = (bytes) => {
    if (bytes === null || bytes === undefined) return "—";
    const units = ["B", "KB", "MB", "GB", "TB"];
    let value = bytes;
    let i = 0;
    while (value >= 1024 && i < units.length - 1) {
      value = value / 1024;
      i += 1;
    }
    return value.toFixed(value >= 10 || i === 0 ? 0 : 1) + " " + units[i];
  };

  const formatDateTime = (value) => {
    if (!value) return "—";
    return new Date(value).toLocaleString(getLocale());
  };

  const showMsg = (text, type = "success") => {
    setMsg({ text, type });
    setTimeout(() => setMsg(null), 3000);
  };

  const fetchBackupSummary = useCallback(async () => {
    try {
      const r = await fetch(`${API}/superadmin/backups/summary`, { headers: headers() });
      if (r.ok) setBackupSummary(await r.json());
    } catch {}
  }, []);

  const fetchHealthSummary = useCallback(async () => {
    try {
      const r = await fetch(`${API}/superadmin/health/summary`, { headers: headers() });
      if (r.ok) setHealthSummary(await r.json());
    } catch {}
  }, []);

  const fetchPlans = useCallback(async () => {
    try {
      const r = await fetch(`${API}/saas/plans`, { headers: headers() });
      if (r.ok) { const data = await r.json(); setPlans(data.plans || data || []); }
    } catch {}
  }, []);

  const fetchSubscriptions = useCallback(async () => {
    try {
      const r = await fetch(`${API}/saas/subscriptions`, { headers: headers() });
      if (r.ok) { const data = await r.json(); setSubscriptions(data.subscriptions || data || []); }
    } catch {}
  }, []);

  const [billingPreview, setBillingPreview] = useState(null);

  const [moduleAddonModal, setModuleAddonModal] = useState(null);
  const [moduleAddonData, setModuleAddonData] = useState(null);
  const [moduleAddonLoading, setModuleAddonLoading] = useState(false);
  const [moduleAddonSaving, setModuleAddonSaving] = useState(false);
  const [moduleAddonForm, setModuleAddonForm] = useState({
    module_key: "",
    enabled: true,
    grant_type: "gift",
    monthly_price: "0",
    yearly_price: "0",
    currency: "TRY",
    starts_at: "",
    expires_at: "",
    notes: ""
  });
  const [billableMachineModal, setBillableMachineModal] = useState(null);
  const [billableMachineCount, setBillableMachineCount] = useState("");
  const [billableMachineSaving, setBillableMachineSaving] = useState(false);

  const [tenantMachinePriceModal, setTenantMachinePriceModal] = useState(null);
  const [tenantMachinePriceData, setTenantMachinePriceData] = useState(null);
  const [tenantMachinePriceValue, setTenantMachinePriceValue] = useState("");
  const [tenantMachinePriceSaving, setTenantMachinePriceSaving] = useState(false);

  const fetchBillingPreview = useCallback(async () => {
    try {
      const r = await fetch(
        `${API}/superadmin/commercial/billing-preview`,
        { headers: headers() }
      );

      if (!r.ok) {
        setBillingPreview(null);
        return;
      }

      const data = await r.json();
      setBillingPreview(data);
    } catch {
      setBillingPreview(null);
    }
  }, []);

  const openBillableMachineModal = (tenantRow) => {
    if (!tenantRow || Number(tenantRow.tenant_id) === 1) return;

    setBillableMachineModal(tenantRow);

    setBillableMachineCount(
      tenantRow.licensed_machine_count === null ||
      tenantRow.licensed_machine_count === undefined
        ? String(tenantRow.actual_machine_count || 0)
        : String(tenantRow.licensed_machine_count)
    );
  };

  const saveBillableMachineCount = async () => {
    if (!billableMachineModal) return;

    const parsed = Number(billableMachineCount);
    const minimum = Number(
      billableMachineModal.actual_machine_count || 0
    );

    if (
      !Number.isInteger(parsed) ||
      parsed < minimum ||
      parsed >= 100000
    ) {
      alert(
        `Makine adedi ${minimum} veya daha büyük, ` +
        `100000'den küçük tam sayı olmalıdır.`
      );
      return;
    }

    try {
      setBillableMachineSaving(true);

      const r = await fetch(
        `${API}/superadmin/tenants/${billableMachineModal.tenant_id}/machine-license`,
        {
          method: "PUT",
          headers: headers(),
          body: JSON.stringify({
            licensed_machine_count: parsed
          })
        }
      );

      const data = await r.json().catch(() => ({}));

      if (!r.ok) {
        throw new Error(
          data.detail || `HTTP ${r.status}`
        );
      }

      await fetchBillingPreview();

      setBillableMachineModal(null);
      setBillableMachineCount("");

      alert("Faturalandırılan makine adedi güncellendi.");
    } catch (err) {
      console.error(
        "Billable machine update failed:",
        err
      );

      alert(
        err.message ||
        "Makine lisans adedi güncellenemedi."
      );
    } finally {
      setBillableMachineSaving(false);
    }
  };

  const resetModuleAddonForm = () => {
    setModuleAddonForm({
      module_key: "",
      enabled: true,
      grant_type: "gift",
      monthly_price: "0",
      yearly_price: "0",
      currency: "TRY",
      starts_at: "",
      expires_at: "",
      notes: ""
    });
  };

  const fetchTenantModuleOverrides = async (tenantRow) => {
    const tenantId = Number(tenantRow?.tenant_id || tenantRow?.id);

    if (!tenantId) return null;

    try {
      setModuleAddonLoading(true);

      const r = await fetch(
        `${API}/superadmin/tenants/${tenantId}/module-overrides`,
        { headers: headers() }
      );

      const data = await r.json().catch(() => ({}));

      if (!r.ok) {
        throw new Error(data.detail || `HTTP ${r.status}`);
      }

      setModuleAddonData(data);
      return data;
    } catch (err) {
      console.error("Tenant module overrides load failed:", err);
      alert(err.message || sa.moduleAddonLoadFailed);
      return null;
    } finally {
      setModuleAddonLoading(false);
    }
  };

  const openModuleAddonModal = async (tenantRow) => {
    const tenantId = Number(tenantRow?.tenant_id || tenantRow?.id);

    if (!tenantId || tenantId === 1) return;

    const normalizedTenant = {
      ...tenantRow,
      tenant_id: tenantId
    };

    setModuleAddonModal(normalizedTenant);
    setModuleAddonData(null);
    resetModuleAddonForm();

    setModuleAddonForm(prev => ({
      ...prev,
      currency:
        tenantRow?.currency ||
        tenantRow?.billing_currency ||
        "TRY"
    }));

    await fetchTenantModuleOverrides(normalizedTenant);
  };

  const editModuleAddon = (override) => {
    if (!override) return;

    setModuleAddonForm({
      module_key: override.module_key || "",
      enabled: Number(override.enabled || 0) === 1,
      grant_type: override.grant_type || "manual",
      monthly_price: String(override.monthly_price ?? 0),
      yearly_price: String(override.yearly_price ?? 0),
      currency: override.currency || "TRY",
      starts_at: override.starts_at
        ? String(override.starts_at).slice(0, 10)
        : "",
      expires_at: override.expires_at
        ? String(override.expires_at).slice(0, 10)
        : "",
      notes: override.notes || ""
    });
  };

  const saveModuleAddon = async () => {
    if (!moduleAddonModal) return;

    const moduleKey = String(
      moduleAddonForm.module_key || ""
    ).trim();

    if (!moduleKey) {
      alert(sa.moduleAddonModuleRequired);
      return;
    }

    const monthlyPrice = Number(
      moduleAddonForm.monthly_price || 0
    );

    const yearlyPrice = Number(
      moduleAddonForm.yearly_price || 0
    );

    if (
      !Number.isFinite(monthlyPrice) ||
      !Number.isFinite(yearlyPrice) ||
      monthlyPrice < 0 ||
      yearlyPrice < 0
    ) {
      alert(sa.moduleAddonInvalidPrice);
      return;
    }

    try {
      setModuleAddonSaving(true);

      const r = await fetch(
        `${API}/superadmin/tenants/${moduleAddonModal.tenant_id}/module-overrides/${encodeURIComponent(moduleKey)}`,
        {
          method: "PUT",
          headers: headers(),
          body: JSON.stringify({
            enabled: Boolean(moduleAddonForm.enabled),
            grant_type: moduleAddonForm.grant_type || "manual",
            monthly_price:
              moduleAddonForm.grant_type === "addon"
                ? monthlyPrice
                : 0,
            yearly_price:
              moduleAddonForm.grant_type === "addon"
                ? yearlyPrice
                : 0,
            currency: moduleAddonForm.currency || "TRY",
            starts_at: moduleAddonForm.starts_at || null,
            expires_at: moduleAddonForm.expires_at || null,
            notes: moduleAddonForm.notes || ""
          })
        }
      );

      const data = await r.json().catch(() => ({}));

      if (!r.ok) {
        throw new Error(data.detail || `HTTP ${r.status}`);
      }

      await fetchTenantModuleOverrides(moduleAddonModal);
      await fetchBillingPreview();

      resetModuleAddonForm();
      alert(sa.moduleAddonSaved);
    } catch (err) {
      console.error(
        "Tenant module override save failed:",
        err
      );

      alert(err.message || sa.moduleAddonSaveFailed);
    } finally {
      setModuleAddonSaving(false);
    }
  };

  const deleteModuleAddon = async (moduleKey) => {
    if (!moduleAddonModal || !moduleKey) return;

    if (!window.confirm(sa.moduleAddonDeleteConfirm)) {
      return;
    }

    try {
      setModuleAddonSaving(true);

      const r = await fetch(
        `${API}/superadmin/tenants/${moduleAddonModal.tenant_id}/module-overrides/${encodeURIComponent(moduleKey)}`,
        {
          method: "DELETE",
          headers: headers()
        }
      );

      const data = await r.json().catch(() => ({}));

      if (!r.ok) {
        throw new Error(data.detail || `HTTP ${r.status}`);
      }

      await fetchTenantModuleOverrides(moduleAddonModal);
      await fetchBillingPreview();

      if (moduleAddonForm.module_key === moduleKey) {
        resetModuleAddonForm();
      }

      alert(sa.moduleAddonDeleted);
    } catch (err) {
      console.error(
        "Tenant module override delete failed:",
        err
      );

      alert(err.message || sa.moduleAddonDeleteFailed);
    } finally {
      setModuleAddonSaving(false);
    }
  };

  const openTenantMachinePriceModal = async (tenantRow) => {
    if (!tenantRow || Number(tenantRow.tenant_id) === 1) return;

    try {
      setTenantMachinePriceSaving(true);

      const r = await fetch(
        `${API}/superadmin/tenants/${tenantRow.tenant_id}/machine-price`,
        { headers: headers() }
      );

      const data = await r.json().catch(() => ({}));

      if (!r.ok) {
        throw new Error(
          data.detail || `HTTP ${r.status}`
        );
      }

      setTenantMachinePriceModal(tenantRow);
      setTenantMachinePriceData(data);

      setTenantMachinePriceValue(
        data.configured && data.override?.monthly_price !== undefined
          ? String(data.override.monthly_price)
          : String(data.machine_unit_price ?? "")
      );
    } catch (err) {
      console.error(
        "Tenant machine price load failed:",
        err
      );

      alert(
        err.message ||
        "Müşteri özel makine fiyatı yüklenemedi."
      );
    } finally {
      setTenantMachinePriceSaving(false);
    }
  };

  const saveTenantMachinePrice = async () => {
    if (!tenantMachinePriceModal || !tenantMachinePriceData) return;

    const price = Number(tenantMachinePriceValue);

    if (!Number.isFinite(price) || price < 0) {
      alert(
        "Makine birim fiyatı 0 veya daha büyük bir sayı olmalıdır."
      );
      return;
    }

    try {
      setTenantMachinePriceSaving(true);

      const r = await fetch(
        `${API}/superadmin/tenants/${tenantMachinePriceModal.tenant_id}/machine-price`,
        {
          method: "PUT",
          headers: headers(),
          body: JSON.stringify({
            currency: tenantMachinePriceData.currency || "TRY",
            monthly_price: price,
            is_active: true,
            starts_at: null,
            expires_at: null,
            notes: "SuperAdmin tenant contract machine price"
          })
        }
      );

      const data = await r.json().catch(() => ({}));

      if (!r.ok) {
        throw new Error(
          data.detail || `HTTP ${r.status}`
        );
      }

      await fetchBillingPreview();

      setTenantMachinePriceData(data);
      setTenantMachinePriceValue(
        String(data.machine_unit_price ?? price)
      );

      alert(
        "Müşteri özel makine fiyatı kaydedildi."
      );
    } catch (err) {
      console.error(
        "Tenant machine price save failed:",
        err
      );

      alert(
        err.message ||
        "Müşteri özel makine fiyatı kaydedilemedi."
      );
    } finally {
      setTenantMachinePriceSaving(false);
    }
  };

  const deleteTenantMachinePrice = async () => {
    if (!tenantMachinePriceModal || !tenantMachinePriceData) return;

    if (!tenantMachinePriceData.configured) {
      return;
    }

    if (
      !window.confirm(
        "Müşteri özel fiyatı silinsin ve paket fiyatına dönülsün mü?"
      )
    ) {
      return;
    }

    try {
      setTenantMachinePriceSaving(true);

      const currency =
        tenantMachinePriceData.currency || "TRY";

      const r = await fetch(
        `${API}/superadmin/tenants/${tenantMachinePriceModal.tenant_id}/machine-price?currency=${encodeURIComponent(currency)}`,
        {
          method: "DELETE",
          headers: headers()
        }
      );

      const data = await r.json().catch(() => ({}));

      if (!r.ok) {
        throw new Error(
          data.detail || `HTTP ${r.status}`
        );
      }

      const getResponse = await fetch(
        `${API}/superadmin/tenants/${tenantMachinePriceModal.tenant_id}/machine-price`,
        { headers: headers() }
      );

      const fresh = await getResponse.json().catch(() => ({}));

      if (!getResponse.ok) {
        throw new Error(
          fresh.detail || `HTTP ${getResponse.status}`
        );
      }

      await fetchBillingPreview();

      setTenantMachinePriceData(fresh);
      setTenantMachinePriceValue(
        String(fresh.machine_unit_price ?? "")
      );

      alert(
        "Özel fiyat kaldırıldı. Paket fiyatına dönüldü."
      );
    } catch (err) {
      console.error(
        "Tenant machine price delete failed:",
        err
      );

      alert(
        err.message ||
        "Müşteri özel makine fiyatı silinemedi."
      );
    } finally {
      setTenantMachinePriceSaving(false);
    }
  };

  const fetchMachinePrices = useCallback(async () => {
    try {
      setMachinePriceLoading(true);

      const res = await fetch(
        `${API}/superadmin/commercial/machine-prices`,
        { headers: headers() }
      );

      if (!res.ok) {
        throw new Error(`HTTP ${res.status}`);
      }

      const data = await res.json();

      setMachinePriceCatalog(
        Array.isArray(data?.plans) ? data.plans : []
      );
    } catch (err) {
      console.error("Machine price catalog load failed:", err);
      setMachinePriceCatalog([]);
    } finally {
      setMachinePriceLoading(false);
    }
  }, []);

  const fetchErpConnectors = useCallback(async (subscriptionRows = null) => {
    try {
      let rows = subscriptionRows;

      if (!rows) {
        const r = await fetch(`${API}/saas/subscriptions`, { headers: headers() });
        if (!r.ok) return;

        const data = await r.json();
        rows = data.subscriptions || data || [];
      }

      const uniqueTenantIds = [
        ...new Set(
          (rows || [])
            .map(item => Number(item.tenant_id))
            .filter(Boolean)
        )
      ];

      const results = await Promise.all(
        uniqueTenantIds.map(async tenantId => {
          try {
            const r = await fetch(
              `${API}/superadmin/tenants/${tenantId}/erp-connector`,
              { headers: headers() }
            );

            if (!r.ok) return [tenantId, null];

            const data = await r.json();
            return [tenantId, data.connector || null];
          } catch {
            return [tenantId, null];
          }
        })
      );

      const mapped = {};
      results.forEach(([tenantId, connector]) => {
        if (connector) mapped[tenantId] = connector;
      });

      setErpConnectors(mapped);
    } catch {}
  }, []);

  const runBackup = async () => {
    if (window.confirm(sa.confirmManualBackup) === false) return;

    setBackupRunning(true);
    try {
      const r = await fetch(`${API}/superadmin/backups/run`, {
        method: "POST",
        headers: headers()
      });
      const data = await r.json();

      if (r.ok === false) {
        showMsg(data.detail || sa.backupFailed, "error");
      } else {
        showMsg(sa.backupCreated);
        fetchBackupSummary();
      }
    } catch {
      showMsg(sa.backupConnectionError, "error");
    }
    setBackupRunning(false);
  };

  const fetchTenants = useCallback(async () => {
    setLoading(true);
    try {
      const r = await fetch(`${API}/superadmin/tenants`, { headers: headers() });
      if (r.ok) setTenants(await r.json());
    } catch {}
    setLoading(false);
  }, []);

  const fetchUsers = useCallback(async () => {
    setLoading(true);
    try {
      const r = await fetch(`${API}/superadmin/users`, { headers: headers() });
      if (r.ok) { const data = await r.json(); setUsers(data.users || data || []); }
    } catch {}
    setLoading(false);
  }, []);

  useEffect(() => {
    if (
      menu === "dashboard" ||
      menu === "tenants" ||
      menu === "backups" ||
      menu === "edge"
    ) {
      fetchTenants();
    }


    if (menu === "dashboard") {
      fetchBillingPreview();
    }

    if (menu === "dashboard" || menu === "users") {
      fetchUsers();
    }

    if (menu === "backups" || menu === "health") {
      fetchBackupSummary();
    }
    if (menu === "health") {
      fetchHealthSummary();
    }

    if (menu === "licenses") {
      fetchPlans();
      fetchSubscriptions();
      fetchErpConnectors();
      fetchMachinePrices();
      fetchBillingPreview();
    }
  }, [
    menu,
    fetchTenants,
    fetchUsers,
    fetchBackupSummary,
    fetchHealthSummary,
    fetchBillingPreview,
    fetchPlans,
    fetchSubscriptions,
    fetchErpConnectors,
    fetchMachinePrices
  ]);

  const saveSubscription = async () => {
    try {
      const r = await fetch(`${API}/superadmin/tenants/${form.tenant_id}/subscription`, {
        method: "PATCH",
        headers: headers(),
        body: JSON.stringify({
          plan_code: form.plan_code,
          currency: form.currency,
          status: form.status,
          paid_until: form.paid_until || null,
          trial_ends_at: form.trial_ends_at || null
        })
      });

      const data = await r.json();

      if (r.ok) {
        showMsg(sa.licenseUpdated);
        setModal(null);
        fetchSubscriptions();
        fetchTenants();
      } else {
        showMsg(data.detail || sa.licenseUpdateFailed, "error");
      }
    } catch {
      showMsg(sa.licenseConnectionError, "error");
    }
  };

  const openMachinePriceModal = (plan) => {
    if (!plan?.plan_code) return;

    setForm({
      plan_code: plan.plan_code,
      plan_name:
        PLAN_LABELS[plan.plan_code] ||
        plan.plan_name ||
        plan.plan_code,
      prices: {
        TRY: String(plan?.prices?.TRY ?? 0),
        USD: String(plan?.prices?.USD ?? 0),
        EUR: String(plan?.prices?.EUR ?? 0)
      }
    });

    setModal("machinePrice");
  };

  const saveMachinePrice = async () => {
    const planCode = form?.plan_code;

    if (!planCode) {
      alert(sa.machinePricePlanMissing);
      return;
    }

    const prices = {};

    for (const currency of ["TRY", "USD", "EUR"]) {
      const raw = form?.prices?.[currency];

      if (
        raw === "" ||
        raw === null ||
        raw === undefined ||
        Number.isNaN(Number(raw)) ||
        Number(raw) < 0
      ) {
        alert(sa.machinePriceInvalid);
        return;
      }

      prices[currency] = Number(raw);
    }

    try {
      const res = await fetch(
        `${API}/superadmin/commercial/machine-prices/${planCode}`,
        {
          method: "PUT",
          headers: headers(),
          body: JSON.stringify({ prices })
        }
      );

      const data = await res.json().catch(() => ({}));

      if (!res.ok) {
        throw new Error(
          data?.detail ||
          data?.message ||
          `HTTP ${res.status}`
        );
      }

      await fetchMachinePrices();
      setModal(null);
      alert(sa.machinePriceSaved);
    } catch (err) {
      console.error("Machine price save failed:", err);
      alert(sa.machinePriceSaveFailed);
    }
  };

  const saveErpConnector = async () => {
    const tenantId = Number(form.tenant_id);

    if (!tenantId) {
      showMsg(sa.erpConnectorTenantMissing, "error");
      return;
    }

    const monthlyPrice = Number(form.monthly_price || 0);
    const yearlyPrice = Number(form.yearly_price || 0);

    if (
      !Number.isFinite(monthlyPrice) ||
      !Number.isFinite(yearlyPrice) ||
      monthlyPrice < 0 ||
      yearlyPrice < 0
    ) {
      showMsg(sa.erpConnectorInvalidPrice, "error");
      return;
    }

    try {
      const r = await fetch(
        `${API}/superadmin/tenants/${tenantId}/erp-connector`,
        {
          method: "PUT",
          headers: headers(),
          body: JSON.stringify({
            enabled: !!form.enabled,
            grant_type: form.grant_type || "addon",
            monthly_price: monthlyPrice,
            yearly_price: yearlyPrice,
            currency: form.currency || "TRY",
            starts_at: form.starts_at || null,
            expires_at: form.expires_at || null,
            notes: form.notes || null
          })
        }
      );

      const data = await r.json();

      if (!r.ok) {
        showMsg(data.detail || sa.erpConnectorSaveFailed, "error");
        return;
      }

      setErpConnectors(prev => ({
        ...prev,
        [tenantId]: data.connector || {}
      }));

      showMsg(sa.erpConnectorSaved);
      setModal(null);
    } catch {
      showMsg(sa.erpConnectorConnectionError, "error");
    }
  };


  const saveTenant = async () => {
    // P7.6F create tenant machine license guard
    if (!form.id) {
      const rawMachineLicense = form.licensed_machine_count;

      if (
        rawMachineLicense === "" ||
        rawMachineLicense === null ||
        rawMachineLicense === undefined
      ) {
        alert("Lisanslı makine adedi zorunludur.");
        return;
      }

      const parsedMachineLicense = Number(rawMachineLicense);

      if (
        !Number.isInteger(parsedMachineLicense) ||
        parsedMachineLicense < 0 ||
        parsedMachineLicense >= 100000
      ) {
        alert(
          "Lisanslı makine adedi 0 ile 99999 arasında tam sayı olmalıdır."
        );
        return;
      }
    }

    try {
      const method = form.id ? "PATCH" : "POST";
      const url = form.id ? `${API}/superadmin/tenants/${form.id}` : `${API}/superadmin/tenants`;
      const r = await fetch(url, { method, headers: headers(), body: JSON.stringify(form) });
      if (r.ok) {
        if (form.id && form.plan) {
          await fetch(`${API}/saas/tenants/${form.id}/plan`, {
            method: "PUT",
            headers: headers(),
            body: JSON.stringify({
              plan_code: form.plan,
              status: form.is_active ? "active" : "cancelled",
              paid_until: form.paid_until || null,
              trial_ends_at: form.trial_ends_at || null
            })
          });
        }
        showMsg("Kaydedildi ✓");
        setModal(null);
        fetchTenants();
        fetchSubscriptions();
      }
      else showMsg(sa.genericError, "error");
    } catch { showMsg(sa.connectionError, "error"); }
  };

  const toggleTenant = async (id, current) => {
    await fetch(`${API}/superadmin/tenants/${id}`, {
      method: "PATCH", headers: headers(),
      body: JSON.stringify({ is_active: current ? 0 : 1 })
    });
    fetchTenants();
  };

  const deleteTenant = async (id) => {
    if (!window.confirm(sa.confirmDeleteTenant)) return;
    await fetch(`${API}/superadmin/tenants/${id}`, { method: "DELETE", headers: headers() });
    showMsg("Firma silindi");
    fetchTenants();
  };

  const saveUser = async () => {
    try {
      const r = await fetch(`${API}/superadmin/users`, {
        method: "POST", headers: headers(), body: JSON.stringify(form)
      });
      if (r.ok) { showMsg(sa.userCreated); setModal(null); fetchUsers(); }
      else showMsg(sa.genericError, "error");
    } catch { showMsg(sa.connectionError, "error"); }
  };

  const resetPassword = async (userId) => {
    const pwd = window.prompt(sa.promptNewPassword);
    if (!pwd) return;
    const r = await fetch(`${API}/superadmin/users/${userId}/reset-password`, {
      method: "PATCH", headers: headers(), body: JSON.stringify({ password: pwd })
    });
    if (r.ok) showMsg(sa.passwordReset);
    else showMsg("Hata", "error");
  };

  const toggleUser = async (userId) => {
    await fetch(`${API}/superadmin/users/${userId}/toggle`, { method: "PATCH", headers: headers() });
    fetchUsers();
  };

  const editUser = (u) => {
    setForm({
      id: u.id,
      username: u.username,
      role: u.role,
      tenant_id: u.tenant_id || "",
      is_active: !!u.is_active
    });
    setModal("user");
  };

  const updateUser = async () => {
    const r = await fetch(`${API}/superadmin/users/${form.id}`, {
      method: "PATCH",
      headers: headers(),
      body: JSON.stringify({
        username: form.username,
        role: form.role,
        tenant_id: form.tenant_id ? Number(form.tenant_id) : null,
        is_active: !!form.is_active
      })
    });

    if (r.ok) {
      showMsg(sa.userUpdated);
      setModal(null);
      fetchUsers();
    } else {
      const d = await r.json().catch(() => ({}));
      showMsg(d.detail || sa.userUpdateFailed, "error");
    }
  };

  const deleteUser = async (id) => {
    if (!window.confirm(sa.confirmDeleteUser)) return;

    const r = await fetch(`${API}/superadmin/users/${id}`, {
      method: "DELETE",
      headers: headers()
    });

    if (r.ok) {
      showMsg(sa.userDeleted);
      fetchUsers();
    } else {
      const d = await r.json().catch(() => ({}));
      showMsg(d.detail || sa.userDeleteFailed, "error");
    }
  };

  const impersonateTenant = async (tenantId) => {
    if (!window.confirm(sa.confirmImpersonateTenant)) return;

    try {
      const r = await fetch(`${API}/superadmin/impersonate/${tenantId}`, {
        method: "POST",
        headers: headers()
      });

      const data = await r.json();

      if (!r.ok) {
        showMsg(data.detail || sa.tenantLoginError, "error");
        return;
      }

      localStorage.setItem("superadmin_token", localStorage.getItem("token") || "");
      localStorage.setItem("superadmin_username", localStorage.getItem("username") || "");
      localStorage.setItem("superadmin_role", localStorage.getItem("role") || "");

      localStorage.setItem("token", data.access_token);
      localStorage.setItem("username", data.user.username);
      localStorage.setItem("role", data.user.role);
      localStorage.setItem("impersonated_tenant", JSON.stringify(data.tenant));
      localStorage.setItem("impersonated_user", JSON.stringify(data.user));

      showMsg(`${data.tenant.name} hesabına geçiliyor...`);
      setTimeout(() => {
        window.location.href = "/";
      }, 600);
    } catch {
      showMsg(sa.connectionError, "error");
    }
  };

  const extendPlan = async (tenantId) => {
    const months = window.prompt(sa.promptExtendMonths);
    if (!months) return;
    const d = new Date();
    d.setMonth(d.getMonth() + parseInt(months));
    const paid_until = d.toISOString().split("T")[0];
    await fetch(`${API}/superadmin/tenants/${tenantId}`, {
      method: "PATCH", headers: headers(),
      body: JSON.stringify({ paid_until, plan: "professional", is_active: 1 })
    });
    showMsg(`${months} ay uzatıldı ✓`);
    fetchTenants();
  };

  const S = {
    wrap: { minHeight: "100vh", background: "#0a0f1a", color: "#e2e8f0", fontFamily: "'DM Sans', 'Segoe UI', sans-serif", display: "flex" },
    sidebar: { width: 260, background: "#080d16", borderRight: "1px solid #1e3a5f", padding: 20, boxSizing: "border-box", position: "sticky", top: 0, height: "100vh" },
    content: { flex: 1, minWidth: 0 },
    sideNav: { display: "flex", flexDirection: "column", gap: 8, marginTop: 28 },
    sideItem: (active) => ({ width: "100%", display: "flex", alignItems: "center", gap: 10, padding: "11px 12px", borderRadius: 12, border: "1px solid " + (active ? "#2d7dd2" : "transparent"), background: active ? "rgba(45,125,210,0.18)" : "transparent", color: active ? "#fff" : "#94a3b8", cursor: "pointer", fontSize: 13, fontWeight: 700, textAlign: "left" }),
    header: { background: "linear-gradient(135deg, #0f1923 0%, #1a2535 100%)", borderBottom: "1px solid #1e3a5f", padding: "0 32px", height: 64, display: "flex", alignItems: "center", justifyContent: "space-between" },
    logo: { display: "flex", alignItems: "center", gap: 12 },
    logoIcon: { width: 36, height: 36, background: "linear-gradient(135deg, #2d7dd2, #1d5fa8)", borderRadius: 10, display: "flex", alignItems: "center", justifyContent: "center", fontSize: 18 },
    logoText: { fontSize: 16, fontWeight: 700, color: "#fff", letterSpacing: "-0.3px" },
    badge: { background: "#1e3a5f", color: "#60a5fa", fontSize: 11, fontWeight: 600, padding: "3px 10px", borderRadius: 20, letterSpacing: 1 },
    main: { padding: 32, maxWidth: 1400, margin: "0 auto" },
    tabs: { display: "flex", gap: 4, marginBottom: 28, background: "#0f1923", padding: 4, borderRadius: 12, width: "fit-content", border: "1px solid #1e3a5f" },
    tab: (active) => ({ padding: "8px 20px", borderRadius: 9, cursor: "pointer", fontSize: 13, fontWeight: 600, transition: "all 0.2s", background: active ? "#2d7dd2" : "transparent", color: active ? "#fff" : "#64748b", border: "none" }),
    card: { background: "#0f1923", border: "1px solid #1e3a5f", borderRadius: 16, overflow: "hidden" },
    cardHeader: { padding: "16px 24px", borderBottom: "1px solid #1e3a5f", display: "flex", justifyContent: "space-between", alignItems: "center" },
    cardTitle: { fontSize: 15, fontWeight: 700, color: "#f1f5f9" },
    btn: (color = "#2d7dd2") => ({ background: color, color: "#fff", border: "none", borderRadius: 8, padding: "8px 16px", fontSize: 13, fontWeight: 600, cursor: "pointer" }),
    btnSm: (color = "#1e3a5f") => ({ background: color, color: "#fff", border: "none", borderRadius: 6, padding: "5px 10px", fontSize: 11, fontWeight: 600, cursor: "pointer" }),
    table: { width: "100%", borderCollapse: "collapse" },
    th: { padding: "12px 16px", textAlign: "left", fontSize: 11, fontWeight: 600, color: "#64748b", letterSpacing: 1, textTransform: "uppercase", borderBottom: "1px solid #1e3a5f" },
    td: { padding: "14px 16px", fontSize: 13, color: "#cbd5e1", borderBottom: "1px solid #0f1923" },
    overlay: { position: "fixed", inset: 0, background: "rgba(0,0,0,0.7)", display: "flex", alignItems: "center", justifyContent: "center", zIndex: 1000 },
    modalBox: { background: "#0f1923", border: "1px solid #1e3a5f", borderRadius: 16, padding: 28, width: 480, maxWidth: "90vw" },
    input: { width: "100%", background: "#0a0f1a", border: "1px solid #1e3a5f", borderRadius: 8, padding: "10px 14px", color: "#e2e8f0", fontSize: 13, outline: "none", boxSizing: "border-box", marginBottom: 12 },
    label: { fontSize: 12, color: "#64748b", fontWeight: 600, marginBottom: 4, display: "block" },
    row: { display: "grid", gridTemplateColumns: "1fr 1fr", gap: 12 },
    toast: (type) => ({ position: "fixed", top: 24, right: 24, background: type === "error" ? "#7f1d1d" : "#14532d", color: type === "error" ? "#fca5a5" : "#86efac", padding: "12px 20px", borderRadius: 10, fontSize: 13, fontWeight: 600, zIndex: 9999, border: `1px solid ${type === "error" ? "#991b1b" : "#166534"}` }),
  };

  const daysLeft = (dateValue) => {
    if (!dateValue) return null;
    const end = new Date(dateValue);
    const now = new Date();
    return Math.ceil((end - now) / (1000 * 60 * 60 * 24));
  };


  const filteredUsers =
    userTenantFilter === "all"
      ? users
      : users.filter(u => String(u.tenant_id) === String(userTenantFilter));

  const stats = {
    total: tenants.length,
    active: tenants.filter(t => t.is_active).length,
    demo: tenants.filter(t => t.is_demo).length,
    paid: tenants.filter(t => !t.is_demo && t.is_active).length,
    users: users.length,
    renewSoon: tenants.filter(t => {
      const left = daysLeft(t.paid_until || t.trial_ends_at);
      return left !== null && left >= 0 && left <= 7;
    }).length,
    expired: tenants.filter(t => {
      const left = daysLeft(t.paid_until || t.trial_ends_at);
      return left !== null && left < 0;
    }).length,
    mrrByCurrency: billingPreview?.summary?.mrr_by_currency || {
      TRY: 0,
      USD: 0,
      EUR: 0
    },
  };

  return (
    <div style={S.wrap}>
      {msg && <div style={S.toast(msg.type)}>{msg.text}</div>}

      <aside style={S.sidebar}>
        <div style={S.logo}>
          <div style={S.logoIcon}>⚙️</div>
          <div>
            <div style={S.logoText}>Opement</div>
            <div style={{ fontSize: 10, color: "#64748b", letterSpacing: 0.5 }}>SaaS Admin</div>
          </div>
        </div>

        <nav style={S.sideNav}>
          {MENU_ITEMS.map(item => (
            <button key={item.key} style={S.sideItem(menu === item.key)} onClick={() => setMenu(item.key)}>
              <span>{item.icon}</span>
              <span>{sa.menu[item.key]}</span>
            </button>
          ))}
        </nav>
      </aside>

      <div style={S.content}>
      <header style={S.header}>
        <div style={S.logo}>
          <div style={S.logoIcon}>⚙️</div>
          <div>
            <div style={S.logoText}>Opement</div>
            <div style={{ fontSize: 10, color: "#64748b", letterSpacing: 0.5 }}>{sa.superAdminTitle}</div>
          </div>
        </div>
        <div style={{ display: 'flex', alignItems: 'center', gap: 12 }}>
            <div style={S.badge}>SUPER ADMIN</div>
            <button style={{ background: '#7f1d1d', color: '#fca5a5', border: 'none', borderRadius: 8, padding: '6px 14px', fontSize: 12, fontWeight: 600, cursor: 'pointer' }}
                onClick={() => { localStorage.removeItem('token'); window.location.href = '/login'; }}>
                Çıkış
            </button>
            </div>
      </header>

      <main style={S.main}>
        {/* İstatistikler */}
        <div style={{ display: "grid", gridTemplateColumns: "repeat(7, 1fr)", gap: 16, marginBottom: 28 }}>
          {[
            { label: "Toplam Firma", value: stats.total, color: "#3b82f6", icon: "🏭" },
            { label: "Aktif Firma", value: stats.active, color: "#10b981", icon: "✅" },
            { label: "Demo Hesap", value: stats.demo, color: "#f59e0b", icon: "🎯" },
            { label: sa.user, value: stats.users, color: "#06b6d4", icon: "👥" },
            { label: sa.renewalSoon, value: stats.renewSoon, color: "#f97316", icon: "⏰" },
            { label: sa.expired, value: stats.expired, color: "#ef4444", icon: "🚨" },
            {
              label: "MRR",
              value: [
                `₺${Number(stats.mrrByCurrency.TRY || 0).toLocaleString("tr-TR")}`,
                `$${Number(stats.mrrByCurrency.USD || 0).toLocaleString("en-US")}`,
                `€${Number(stats.mrrByCurrency.EUR || 0).toLocaleString("de-DE")}`
              ].join(" · "),
              color: "#8b5cf6",
              icon: "💰"
            },
          ].map((s, i) => (
            <div key={i} style={{ ...S.card, padding: 20 }}>
              <div style={{ fontSize: 24, marginBottom: 8 }}>{s.icon}</div>
              <div style={{ fontSize: 28, fontWeight: 700, color: s.color }}>{s.value}</div>
              <div style={{ fontSize: 12, color: "#64748b", marginTop: 2 }}>{s.label}</div>
            </div>
          ))}
        </div>

        {menu === "dashboard" && (
          <div style={{ display: "grid", gridTemplateColumns: "2fr 1fr", gap: 18 }}>
            <div style={{ ...S.card, padding: 24 }}>
              <div style={S.cardTitle}>{sa.saasOverview}</div>
              <p style={{ color: "#94a3b8", fontSize: 13, lineHeight: 1.7 }}>
                Opement tenant, lisans, ödeme, demo ve müşteri yaşam döngüsü buradan yönetilecek.
              </p>
            </div>

            <div style={{ ...S.card, padding: 24 }}>
              <div style={S.cardTitle}>{sa.upcomingWork}</div>
              <div style={{ marginTop: 14, color: "#94a3b8", fontSize: 13, lineHeight: 1.8 }}>
                <div>{sa.upcomingLicenseCenter}</div>
                <div>{sa.upcomingPaymentArchitecture}</div>
                <div>{sa.upcomingCrmPipeline}</div>
                <div>{sa.upcomingServerHealth}</div>
              </div>
            </div>
          </div>
        )}

        {/* Firmalar */}
        {menu === "tenants" && (
          <div>
            <div style={{ ...S.cardHeader, ...S.card, marginBottom: 18 }}>
              <div>
                <div style={S.cardTitle}>{sa.tenantManagement}</div>
                <div style={{ fontSize: 12, color: "#64748b", marginTop: 4 }}>
                  Tenant lisansları, ödeme durumu ve firma erişimleri
                </div>
              </div>
              <button style={S.btn()} onClick={() => { setForm({
                  is_active: 1,
                  is_demo: 0,
                  plan: "trial",
                  data_source: "simulation",
                  licensed_machine_count: ""
                }); setModal("tenant"); }}>
                + Yeni Firma
              </button>
            </div>

            {loading ? (
              <div style={{ padding: 40, textAlign: "center", color: "#64748b" }}>{sa.loading}</div>
            ) : (
              <div style={{ display: "grid", gridTemplateColumns: "repeat(auto-fill, minmax(330px, 1fr))", gap: 18 }}>
                {tenants.map(t => {
                  const left = daysLeft(t.paid_until || t.trial_ends_at);
                  const tenantUsers = users.filter(u => String(u.tenant_id) === String(t.id)).length;

                  return (
                    <div key={t.id} style={{ ...S.card, padding: 20 }}>
                      <div style={{ display: "flex", justifyContent: "space-between", gap: 12 }}>
                        <div>
                          <div style={{ fontSize: 17, fontWeight: 800, color: "#f8fafc" }}>{t.name}</div>
                          <div style={{ fontSize: 12, color: "#60a5fa", marginTop: 5, fontFamily: "monospace" }}>
                            {t.subdomain}.opement.com
                          </div>
                        </div>
                        <span style={{ height: 24, background: t.is_active ? "#14532d" : "#7f1d1d", color: t.is_active ? "#86efac" : "#fca5a5", padding: "4px 10px", borderRadius: 20, fontSize: 11, fontWeight: 800 }}>
                          {t.is_active ? "Aktif" : "Pasif"}
                        </span>
                      </div>

                      <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: 10, marginTop: 18 }}>
                        <div style={{ background: "#0a0f1a", border: "1px solid #1e3a5f", borderRadius: 12, padding: 12 }}>
                          <div style={{ fontSize: 11, color: "#64748b" }}>{sa.plan}</div>
                          <div style={{ marginTop: 6, color: PLAN_COLORS[t.plan] || "#93c5fd", fontWeight: 800 }}>
                            {(t.plan === "trial" ? sa.planTrial : PLAN_LABELS[t.plan]) || t.plan || "—"}
                          </div>
                        </div>

                        <div style={{ background: "#0a0f1a", border: "1px solid #1e3a5f", borderRadius: 12, padding: 12 }}>
                          <div style={{ fontSize: 11, color: "#64748b" }}>{sa.user}</div>
                          <div style={{ marginTop: 6, color: "#e2e8f0", fontWeight: 800 }}>
                            {tenantUsers}
                          </div>
                        </div>

                        <div style={{ background: "#0a0f1a", border: "1px solid #1e3a5f", borderRadius: 12, padding: 12 }}>
                          <div style={{ fontSize: 11, color: "#64748b" }}>{sa.dataSource}</div>
                          <div style={{ marginTop: 6, color: t.data_source === "real" ? "#86efac" : "#93c5fd", fontWeight: 800 }}>
                            {t.data_source === "real" ? sa.realMachine : sa.simulation}
                          </div>
                        </div>

                        <div style={{ background: "#0a0f1a", border: "1px solid #1e3a5f", borderRadius: 12, padding: 12 }}>
                          <div style={{ fontSize: 11, color: "#64748b" }}>{sa.license}</div>
                          <div style={{ marginTop: 6, color: left !== null && left < 0 ? "#fca5a5" : "#e2e8f0", fontWeight: 800 }}>
                            {left === null ? "—" : left < 0 ? sa.expired : `${left} ${sa.days}`}
                          </div>
                        </div>
                      </div>

                      <div style={{ marginTop: 16, fontSize: 12, color: "#64748b" }}>
                        {t.contact_email || "E-posta yok"}
                      </div>

                      <div style={{ display: "flex", gap: 8, flexWrap: "wrap", marginTop: 18 }}>
                        <button style={S.btnSm("#0f766e")} onClick={() => impersonateTenant(t.id)}>{sa.signIn}</button>
                        <button style={S.btnSm("#1e3a5f")} onClick={() => { setForm({ ...t }); setModal("tenant"); }}>{sa.edit}</button>
                        <button style={S.btnSm("#14532d")} onClick={() => extendPlan(t.id)}>{sa.extend}</button>
                        <button style={S.btnSm(t.is_active ? "#7f1d1d" : "#14532d")} onClick={() => toggleTenant(t.id, t.is_active)}>
                          {t.is_active ? "Pasif" : "Aktif"}
                        </button>
                        <button style={S.btnSm("#450a0a")} onClick={() => deleteTenant(t.id)}>{sa.delete}</button>
                      </div>
                    </div>
                  );
                })}
              </div>
            )}
          </div>
        )}

        {/* Lisanslar */}
        {menu === "licenses" && (
          <div>
            <div style={{ ...S.cardHeader, ...S.card, marginBottom: 18 }}>
              <div>
                <div style={S.cardTitle}>{sa.licenseCenter}</div>
                <div style={{ fontSize: 12, color: "#64748b", marginTop: 4 }}>
                  {sa.packageLicenseSubtitle}
                </div>
              </div>
            </div>

            {/* Commercial Package Matrix */}
            <div style={{ marginBottom: 22 }}>
              <div style={{
                display: "flex",
                justifyContent: "space-between",
                alignItems: "flex-end",
                gap: 12,
                marginBottom: 12
              }}>
                <div>
                  <div style={{
                    fontSize: 15,
                    fontWeight: 800,
                    color: "#f8fafc"
                  }}>
                    {sa.packageMatrixTitle}
                  </div>

                  <div style={{
                    fontSize: 12,
                    color: "#64748b",
                    marginTop: 4
                  }}>
                    {sa.packageMatrixSubtitle}
                  </div>
                </div>
              </div>

              <div style={{
                display: "grid",
                gridTemplateColumns: "repeat(auto-fit, minmax(220px, 1fr))",
                gap: 12
              }}>
                {["essentials", "professional", "advanced", "enterprise"].map((code) => {
                  const p = plans.find((item) => item.code === code);

                  if (!p) return null;

                  const planColor = PLAN_COLORS[code] || "#3b82f6";

                  const descriptions = {
                    essentials: sa.packageEssentialsDesc,
                    professional: sa.packageProfessionalDesc,
                    advanced: sa.packageAdvancedDesc,
                    enterprise: sa.packageEnterpriseDesc
                  };

                  return (
                    <div
                      key={code}
                      style={{
                        ...S.card,
                        padding: 18,
                        borderTop: `3px solid ${planColor}`
                      }}
                    >
                      <div style={{
                        display: "flex",
                        alignItems: "center",
                        justifyContent: "space-between",
                        gap: 10
                      }}>
                        <div style={{
                          fontSize: 16,
                          fontWeight: 800,
                          color: "#f8fafc"
                        }}>
                          {PLAN_LABELS[code] || p.name || code}
                        </div>

                        <span style={{
                          background: planColor + "20",
                          color: planColor,
                          padding: "4px 9px",
                          borderRadius: 20,
                          fontSize: 11,
                          fontWeight: 800,
                          whiteSpace: "nowrap"
                        }}>
                          {p.module_count || 0} {sa.moduleSuffix}
                        </span>
                      </div>

                      <div style={{
                        minHeight: 48,
                        marginTop: 10,
                        fontSize: 12,
                        lineHeight: 1.5,
                        color: "#94a3b8"
                      }}>
                        {descriptions[code]}
                      </div>

                      <div style={{
                        marginTop: 14,
                        paddingTop: 12,
                        borderTop: "1px solid #1e293b",
                        display: "grid",
                        gap: 7,
                        fontSize: 12,
                        color: "#94a3b8"
                      }}>
                        <div>
                          {sa.usersCommercial}:{" "}
                          <strong style={{ color: "#86efac" }}>
                            {sa.unlimited}
                          </strong>
                        </div>

                        <div>
                          {sa.machineLicensing}:{" "}
                          <strong style={{ color: "#e2e8f0" }}>
                            {sa.perMachineMonthly}
                          </strong>
                        </div>
                      </div>
                    </div>
                  );
                })}
              </div>
            </div>

            <div style={{ display: "grid", gridTemplateColumns: "repeat(auto-fill, minmax(320px, 1fr))", gap: 18 }}>
              {subscriptions.map(s => {
                const left = daysLeft(s.paid_until || s.trial_ends_at);
                const planColor = PLAN_COLORS[s.plan_code] || "#3b82f6";
                return (
                  <div key={s.id} style={{ ...S.card, padding: 20 }}>
                    <div style={{ fontSize: 16, fontWeight: 800, color: "#f8fafc" }}>{s.tenant_name}</div>
                    <div style={{ fontSize: 12, color: "#64748b", marginTop: 5 }}>{s.subdomain}.opement.com</div>

                    <div style={{ marginTop: 16, display: "flex", gap: 8, flexWrap: "wrap" }}>
                      <span style={{ background: planColor + "20", color: planColor, padding: "4px 10px", borderRadius: 20, fontSize: 11, fontWeight: 800 }}>
                        {PLAN_LABELS[s.plan_code] || s.plan_name || s.plan_code || sa.noPlan}
                      </span>
                      <span style={{ background: s.status === "active" ? "#14532d" : s.status === "trial" ? "#451a03" : "#7f1d1d", color: s.status === "active" ? "#86efac" : s.status === "trial" ? "#fcd34d" : "#fca5a5", padding: "4px 10px", borderRadius: 20, fontSize: 11, fontWeight: 800 }}>
                        {s.status}
                      </span>
                    </div>

                    <div style={{ marginTop: 16, display: "grid", gap: 8, fontSize: 13, color: "#94a3b8" }}>
                      <div>{sa.price}: {Number(s.price || 0).toLocaleString(getLocale())} {s.currency}</div>
                      <div>{sa.endDate}: {s.paid_until ? new Date(s.paid_until).toLocaleDateString(getLocale()) : s.trial_ends_at ? new Date(s.trial_ends_at).toLocaleDateString(getLocale()) : "—"}</div>
                      <div>{sa.status}: {left === null ? sa.unknown : left < 0 ? sa.expired : `${left} ${sa.daysRemaining}`}</div>
                      <div>{sa.usersCommercial}: {sa.unlimited}</div>
                      <div>{sa.machineLicensing}: {sa.perMachineMonthly}</div>
                      <div>{sa.email}: {s.contact_email || "—"}</div>

                      <div>
                        {sa.erpConnector}:{" "}
                        <strong
                          style={{
                            color: erpConnectors[s.tenant_id]?.enabled
                              ? "#86efac"
                              : "#94a3b8"
                          }}
                        >
                          {erpConnectors[s.tenant_id]?.enabled
                            ? sa.erpConnectorEnabled
                            : sa.erpConnectorDisabled}
                        </strong>
                      </div>

                      {erpConnectors[s.tenant_id]?.enabled ? (
                        <div>
                          {sa.erpConnectorPrice}:{" "}
                          {Number(
                            erpConnectors[s.tenant_id]?.monthly_price || 0
                          ).toLocaleString(getLocale())}{" "}
                          {erpConnectors[s.tenant_id]?.currency || "TRY"}
                          {" / "}
                          {sa.erpConnectorMonthly}
                        </div>
                      ) : null}
                    </div>

                    <div style={{ display: "flex", gap: 8, flexWrap: "wrap", marginTop: 18 }}>
                      <button style={S.btnSm("#14532d")} onClick={() => extendPlan(s.tenant_id)}>{sa.extend}</button>
                      <button
  style={S.btnSm("#1e3a5f")}
  onClick={() => {
    setForm({
      tenant_id: s.tenant_id,
      plan_code: s.plan_code || "trial",
      currency: s.currency || "TRY",
      status: s.status || "active",
      paid_until: s.paid_until ? String(s.paid_until).slice(0, 10) : "",
      trial_ends_at: s.trial_ends_at ? String(s.trial_ends_at).slice(0, 10) : ""
    });
    setModal("license");
  }}
>
  {sa.editPlan}
</button>

<button
  style={S.btnSm("#5b21b6")}
  onClick={async () => {
    const tenantId = Number(s.tenant_id);

    let connector = erpConnectors[tenantId];

    if (!connector) {
      try {
        const r = await fetch(
          `${API}/superadmin/tenants/${tenantId}/erp-connector`,
          { headers: headers() }
        );

        if (r.ok) {
          const data = await r.json();
          connector = data.connector || {};
          setErpConnectors(prev => ({
            ...prev,
            [tenantId]: connector
          }));
        }
      } catch {}
    }

    connector = connector || {};

    setForm({
      tenant_id: tenantId,
      tenant_name: s.tenant_name || "",
      enabled: !!connector.enabled,
      grant_type: connector.grant_type || "addon",
      monthly_price: connector.monthly_price ?? 0,
      yearly_price: connector.yearly_price ?? 0,
      currency: connector.currency || "TRY",
      starts_at: connector.starts_at
        ? String(connector.starts_at).slice(0, 10)
        : "",
      expires_at: connector.expires_at
        ? String(connector.expires_at).slice(0, 10)
        : "",
      notes: connector.notes || ""
    });

    setModal("erpConnector");
  }}
>
  {sa.erpConnectorManage}
</button>
                    </div>
                  </div>
                );
              })}
            </div>
          </div>
        )}

        {/* Ödemeler */}
        {menu === "payments" && (
          <div>
            <div style={{ ...S.cardHeader, ...S.card, marginBottom: 18 }}>
              <div>
                <div style={S.cardTitle}>{sa.paymentCenter}</div>
                <div style={{ fontSize: 12, color: "#64748b", marginTop: 4 }}>
                  TRY / USD / EUR tahsilat, Stripe + İyzico mimarisi için yönetim ekranı
                </div>
              </div>
            </div>

            <div style={{ display: "grid", gridTemplateColumns: "repeat(3, 1fr)", gap: 18, marginBottom: 18 }}>
              {[
                { title: sa.tryCollection, desc: sa.tryCollectionDesc, icon: "₺" },
                { title: sa.usdCollection, desc: sa.usdCollectionDesc, icon: "$" },
                { title: sa.eurCollection, desc: sa.eurCollectionDesc, icon: "€" },
              ].map((x, i) => (
                <div key={i} style={{ ...S.card, padding: 22 }}>
                  <div style={{ fontSize: 30, fontWeight: 900, color: "#60a5fa" }}>{x.icon}</div>
                  <div style={{ marginTop: 10, fontSize: 16, fontWeight: 800, color: "#f8fafc" }}>{x.title}</div>
                  <div style={{ marginTop: 6, fontSize: 12, color: "#94a3b8" }}>{x.desc}</div>
                </div>
              ))}
            </div>

            <div style={{ ...S.card, padding: 22 }}>
              <div style={S.cardTitle}>{sa.paymentArchitecture}</div>
              <div style={{ marginTop: 14, color: "#94a3b8", fontSize: 13, lineHeight: 1.9 }}>
                <div>{sa.paymentTryInfo}</div>
                <div>{sa.paymentForeignInfo}</div>
                <div>{sa.paymentLicenseLinkInfo}</div>
                <div>{sa.paymentWebhookInfo}</div>
              </div>
            </div>
          </div>
        )}

        {/* CRM */}
        {menu === "crm" && (
          <div>
            <div style={{ ...S.cardHeader, ...S.card, marginBottom: 18 }}>
              <div>
                <div style={S.cardTitle}>{sa.crmPipeline}</div>
                <div style={{ fontSize: 12, color: "#64748b", marginTop: 4 }}>
                  Lead → Demo → Trial → Customer satış yaşam döngüsü
                </div>
              </div>
              <button style={S.btn()} onClick={() => showMsg(sa.crmLeadApiPending)}>
                + Yeni Lead
              </button>
            </div>

            <div style={{ display: "grid", gridTemplateColumns: "repeat(4, 1fr)", gap: 18 }}>
              {[
                { key: "lead", title: sa.crmLead, icon: "🎯", desc: sa.crmLeadDesc },
                { key: "demo", title: sa.crmDemo, icon: "🖥️", desc: sa.crmDemoDesc },
                { key: "trial", title: sa.crmTrial, icon: "🧪", desc: sa.crmTrialDesc },
                { key: "customer", title: sa.crmCustomer, icon: "🏆", desc: sa.crmCustomerDesc },
              ].map(stage => (
                <div key={stage.key} style={{ ...S.card, padding: 18, minHeight: 300 }}>
                  <div style={{ display: "flex", alignItems: "center", gap: 10, marginBottom: 14 }}>
                    <div style={{ fontSize: 24 }}>{stage.icon}</div>
                    <div>
                      <div style={{ fontSize: 16, fontWeight: 800, color: "#f8fafc" }}>{stage.title}</div>
                      <div style={{ fontSize: 11, color: "#64748b" }}>{stage.desc}</div>
                    </div>
                  </div>

                  <div style={{ display: "grid", gap: 10 }}>
                    {stage.key === "trial" && tenants.filter(t => t.is_demo).map(t => (
                      <div key={t.id} style={{ background: "#0a0f1a", border: "1px solid #1e3a5f", borderRadius: 12, padding: 12 }}>
                        <div style={{ fontSize: 13, fontWeight: 800, color: "#f8fafc" }}>{t.name}</div>
                        <div style={{ fontSize: 11, color: "#60a5fa", marginTop: 4 }}>{t.subdomain}.opement.com</div>
                        <div style={{ fontSize: 11, color: "#94a3b8", marginTop: 8 }}>
                          Demo / Trial hesabı
                        </div>
                      </div>
                    ))}

                    {stage.key === "customer" && tenants.filter(t => !t.is_demo && t.is_active).map(t => (
                      <div key={t.id} style={{ background: "#0a0f1a", border: "1px solid #1e3a5f", borderRadius: 12, padding: 12 }}>
                        <div style={{ fontSize: 13, fontWeight: 800, color: "#f8fafc" }}>{t.name}</div>
                        <div style={{ fontSize: 11, color: "#60a5fa", marginTop: 4 }}>{t.subdomain}.opement.com</div>
                        <div style={{ fontSize: 11, color: "#86efac", marginTop: 8 }}>
                          Aktif müşteri
                        </div>
                      </div>
                    ))}

                    {(stage.key === "lead" || stage.key === "demo") && (
                      <div style={{ border: "1px dashed #1e3a5f", borderRadius: 12, padding: 16, color: "#64748b", fontSize: 12, textAlign: "center" }}>
                        Bu aşama için lead kayıt API eklenecek
                      </div>
                    )}
                  </div>
                </div>
              ))}
            </div>
          </div>
        )}


        {/* Sunucu Sağlığı */}
        {menu === "health" && (
          <div>
            <div style={{ ...S.cardHeader, ...S.card, marginBottom: 18 }}>
              <div>
                <div style={S.cardTitle}>{sa.serverHealth}</div>
                <div style={{ fontSize: 12, color: "#64748b", marginTop: 4 }}>
                  Sistem izleme ve operasyon merkezi
                </div>
              </div>
            </div>

            <div style={{ display: "grid", gridTemplateColumns: "repeat(4, 1fr)", gap: 18 }}>
              {[
                ["API", healthSummary?.services?.api === "online" ? "Online" : "—", "#10b981"],
                ["Nginx", healthSummary?.services?.nginx === "online" ? "Online" : "Offline", healthSummary?.services?.nginx === "online" ? "#10b981" : "#ef4444"],
                ["Opement", healthSummary?.services?.opement === "online" ? "Online" : "Offline", healthSummary?.services?.opement === "online" ? "#10b981" : "#ef4444"],
                ["Backup", `${healthSummary?.backup_count ?? backupSummary?.backup_count ?? "—"} adet`, "#3b82f6"],
                ["CPU", healthSummary?.cpu_percent != null ? `${healthSummary.cpu_percent}%` : "—", "#f59e0b"],
                ["RAM", healthSummary?.ram_percent != null ? `${healthSummary.ram_percent}%` : "—", "#f59e0b"],
                ["Disk", healthSummary?.disk_percent != null ? `${healthSummary.disk_percent}%` : "—", "#f59e0b"],
                ["Tenant", String(tenants.length), "#8b5cf6"],
              ].map((x, i) => (
                <div key={i} style={{ ...S.card, padding: 20 }}>
                  <div style={{ fontSize: 12, color: "#64748b" }}>{x[0]}</div>
                  <div style={{ fontSize: 24, fontWeight: 800, color: x[2], marginTop: 10 }}>
                    {x[1]}
                  </div>
                </div>
              ))}
            </div>

            <div style={{ ...S.card, padding: 24, marginTop: 18 }}>
              <div style={S.cardTitle}>{sa.systemInformation}</div>
              <div style={{ marginTop: 12, color: "#94a3b8", lineHeight: 1.9 }}>
                <div>{sa.uptime}: {healthSummary?.uptime || "—"}</div>
                <div>{sa.lastCheck}: {formatDateTime(healthSummary?.checked_at)}</div>
                <div>{sa.lastBackup}: {formatDateTime(backupSummary?.latest?.created_at)}</div>
                <div>{sa.backupSize}: {formatBytes(backupSummary?.latest?.size_bytes)}</div>
                <div>{sa.totalBackups}: {backupSummary?.backup_count ?? "—"}</div>
              </div>
            </div>
          </div>
        )}


        {/* Yedekleme */}
        {menu === "backups" && (
          <div>
            <div style={{ ...S.cardHeader, ...S.card, marginBottom: 18 }}>
              <div>
                <div style={S.cardTitle}>{sa.tenantBackupCenter}</div>
                <div style={{ fontSize: 12, color: "#64748b", marginTop: 4 }}>
                  Firma bazlı manuel ve otomatik yedekleme yönetimi
                </div>
              </div>
            </div>

            <div style={{ display: "grid", gridTemplateColumns: "repeat(auto-fill, minmax(330px, 1fr))", gap: 18 }}>
              {tenants.map(t => (
                <div key={t.id} style={{ ...S.card, padding: 20 }}>
                  <div style={{ fontSize: 16, fontWeight: 800, color: "#f8fafc" }}>{t.name}</div>
                  <div style={{ fontSize: 12, color: "#60a5fa", marginTop: 5, fontFamily: "monospace" }}>
                    {t.subdomain}.opement.com
                  </div>

                  <div style={{ marginTop: 16, display: "grid", gap: 8, color: "#94a3b8", fontSize: 13 }}>
                    <div>{sa.latestBackup}: {formatDateTime(backupSummary?.latest?.created_at)}</div>
                    <div>{sa.latestBackupSize}: {formatBytes(backupSummary?.latest?.size_bytes)}</div>
                    <div>{sa.totalBackups}: {backupSummary?.backup_count ?? "—"}</div>
                    <div>{sa.totalSize}: {formatBytes(backupSummary?.total_size_bytes)}</div>
                    <div>{sa.status}: {backupSummary?.status === "healthy" ? sa.healthy : sa.empty}</div>
                    <div>{sa.plan}: {(t.plan === "trial" ? sa.planTrial : PLAN_LABELS[t.plan]) || t.plan || "—"}</div>
                  </div>

                  <div style={{ display: "flex", gap: 8, flexWrap: "wrap", marginTop: 18 }}>
                    <button style={S.btnSm("#14532d")} onClick={runBackup} disabled={backupRunning}>
                      {backupRunning ? sa.backupRunning : sa.takeBackup}
                    </button>
                    <button style={S.btnSm("#1e3a5f")} onClick={() => showMsg(sa.backupHistoryPending)}>
                      Geçmiş
                    </button>
                    <button style={S.btnSm("#7c2d12")} onClick={() => showMsg(sa.restoreFlowPending)}>
                      Geri Yükle
                    </button>
                  </div>
                </div>
              ))}
            </div>
          </div>
        )}

        {/* Kullanıcılar */}
        
        {menu === "edge" && (
          <SuperAdminEdgeManager tenants={tenants} />
        )}

{menu === "users" && (
          <div style={S.card}>
            <div style={S.cardHeader}>
              <div>
                <div style={S.cardTitle}>{sa.userManagement}</div>
                <div style={{ fontSize: 12, color: "#64748b", marginTop: 4 }}>
                  Toplam {users.length} kullanıcı, seçili görünümde {filteredUsers.length} kullanıcı
                </div>
              </div>

              <div style={{ display: "flex", gap: 10, alignItems: "center" }}>
                <select
                  style={{ ...S.input, width: 230, marginBottom: 0 }}
                  value={userTenantFilter}
                  onChange={e => setUserTenantFilter(e.target.value)}
                >
                  <option value="all">{sa.allCompanies}</option>
                  {tenants.map(t => (
                    <option key={t.id} value={t.id}>{t.name}</option>
                  ))}
                </select>

                <button style={S.btn()} onClick={() => { setForm({ role: "operator", is_active: 1 }); setModal("user"); }}>
                  + Yeni Kullanıcı
                </button>
              </div>
            </div>
            {loading ? <div style={{ padding: 40, textAlign: "center", color: "#64748b" }}>{sa.loading}</div> : (
              <table style={S.table}>
                <thead>
                  <tr>
                    {[
                      sa.user,
                      sa.company,
                      sa.role,
                      sa.status,
                      sa.lastLogin,
                      sa.actions
                    ].map(h => <th key={h} style={S.th}>{h}</th>)}
                  </tr>
                </thead>
                <tbody>
                  {filteredUsers.map(u => (
                    <tr key={u.id} style={{ background: u.id % 2 === 0 ? "#0d1520" : "transparent" }}>
                      <td style={S.td}><div style={{ fontWeight: 600, color: "#f1f5f9" }}>{u.username}</div></td>
                      <td style={S.td}><span style={{ fontSize: 12, color: "#60a5fa" }}>{u.tenant_name || "—"}</span></td>
                      <td style={S.td}><span style={{ background: "#1e3a5f", color: "#93c5fd", padding: "3px 10px", borderRadius: 20, fontSize: 11, fontWeight: 600 }}>{u.role}</span></td>
                      <td style={S.td}>
                        <span style={{ background: u.is_active ? "#14532d" : "#7f1d1d", color: u.is_active ? "#86efac" : "#fca5a5", padding: "3px 10px", borderRadius: 20, fontSize: 11, fontWeight: 600 }}>
                          {u.is_active ? "Aktif" : "Pasif"}
                        </span>
                      </td>
                      <td style={S.td}><span style={{ fontSize: 12, color: "#64748b" }}>{u.last_login ? new Date(u.last_login).toLocaleDateString(getLocale()) : "—"}</span></td>
                      <td style={S.td}>
                        <div style={{ display: "flex", gap: 6, flexWrap: "wrap" }}>
                          <button style={S.btnSm("#2563eb")} onClick={() => editUser(u)}>{sa.edit}</button>
                          <button style={S.btnSm("#1e3a5f")} onClick={() => resetPassword(u.id)}>{sa.password}</button>
                          <button style={S.btnSm(u.is_active ? "#7f1d1d" : "#14532d")} onClick={() => toggleUser(u.id)}>
                            {u.is_active ? "Pasif" : "Aktif"}
                          </button>
                          <button style={S.btnSm("#991b1b")} onClick={() => deleteUser(u.id)}>{sa.delete}</button>
                        </div>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            )}
          </div>
        )}
      {menu === "licenses" && (
        <div
          style={{
            ...S.card,
            marginTop: 18,
            marginBottom: 18
          }}
        >
          <div
            style={{
              display: "flex",
              justifyContent: "space-between",
              alignItems: "flex-start",
              gap: 16,
              marginBottom: 18,
              flexWrap: "wrap"
            }}
          >
            <div>
              <div
                style={{
                  fontSize: 16,
                  fontWeight: 700,
                  color: "#f1f5f9"
                }}
              >
                {sa.machinePricingTitle}
              </div>

              <div
                style={{
                  color: "#94a3b8",
                  fontSize: 13,
                  marginTop: 5
                }}
              >
                {sa.machinePricingSubtitle}
              </div>
            </div>

            <div
              style={{
                padding: "7px 11px",
                borderRadius: 8,
                background: "#0f172a",
                border: "1px solid #334155",
                color: "#cbd5e1",
                fontSize: 12
              }}
            >
              {sa.usersCommercial}: {sa.unlimited}
            </div>
          </div>

          {machinePriceLoading ? (
            <div style={{ color: "#94a3b8", padding: "12px 0" }}>
              {sa.loading}
            </div>
          ) : (
              <>
            
        <div style={{ ...S.card, marginBottom: 18 }}>
          <div
            style={{
              display: "flex",
              alignItems: "center",
              justifyContent: "space-between",
              gap: 16,
              marginBottom: 14
            }}
          >
            <div>
              <div
                style={{
                  fontSize: 16,
                  fontWeight: 700,
                  color: "#f1f5f9"
                }}
              >
                Faturalandırılan Makine Yönetimi
              </div>

              <div
                style={{
                  fontSize: 12,
                  color: "#94a3b8",
                  marginTop: 4
                }}
              >
                Paket MRR hesabı lisanslı makine adedi üzerinden yapılır.
                Gerçek makine sayısının altına düşürülemez.
              </div>
            </div>

            <div
              style={{
                fontSize: 12,
                color: "#94a3b8"
              }}
            >
              Kullanıcı: Sınırsız
            </div>
          </div>

          <div
            style={{
              overflowX: "auto",
              width: "100%",
              WebkitOverflowScrolling: "touch"
            }}
          >
            <table
              style={{
                width: "100%",
                minWidth: 1040,
                borderCollapse: "collapse",
                fontSize: 12
              }}
            >
              <thead>
                <tr>
                  {[
                    "Firma",
                    "Paket",
                    "Gerçek Makine",
                    "Faturalandırılan",
                    "Durum",
                    "Aylık Tutar",
                    ""
                  ].map((h) => (
                    <th
                      key={h}
                      style={{
                        textAlign: "left",
                        padding: "10px 8px",
                        color: "#94a3b8",
                        borderBottom: "1px solid #1e293b",
                        whiteSpace: "nowrap"
                      }}
                    >
                      {h}
                    </th>
                  ))}
                </tr>
              </thead>

              <tbody>
                {(billingPreview?.tenants || []).map((row) => {
                  const stateLabel = {
                    BILLABLE: "Faturalandırılıyor",
                    NOT_CONFIGURED: "Tanımlanmadı",
                    DEMO: "Demo",
                    MASTER_DEMO: "Demo",
                    TEMPLATE: "Şablon",
                    INTERNAL: "Dahili",
                    INACTIVE: "Pasif"
                  }[row.billing_state] || row.billing_state;

                  const planLabel = {
                    essentials: "Essentials",
                    professional: "Professional",
                    advanced: "Advanced",
                    enterprise: "Enterprise",
                    trial: "Trial"
                  }[row.plan_code] || row.plan_code;

                  const symbol = {
                    TRY: "₺",
                    USD: "$",
                    EUR: "€"
                  }[row.currency] || "";

                  return (
                    <tr key={row.tenant_id}>
                      <td
                        style={{
                          padding: "10px 8px",
                          borderBottom: "1px solid #172033",
                          color: "#e2e8f0",
                          whiteSpace: "nowrap"
                        }}
                      >
                        {row.name}
                      </td>

                      <td
                        style={{
                          padding: "10px 8px",
                          borderBottom: "1px solid #172033",
                          color: "#cbd5e1"
                        }}
                      >
                        {planLabel}
                      </td>

                      <td
                        style={{
                          padding: "10px 8px",
                          borderBottom: "1px solid #172033",
                          color: "#cbd5e1"
                        }}
                      >
                        {Number(row.actual_machine_count || 0)}
                      </td>

                      <td
                        style={{
                          padding: "10px 8px",
                          borderBottom: "1px solid #172033",
                          color:
                            row.licensed_machine_count === null
                              ? "#f59e0b"
                              : "#e2e8f0",
                          fontWeight: 600
                        }}
                      >
                        {row.licensed_machine_count === null
                          ? "Tanımlanmadı"
                          : Number(row.licensed_machine_count)}
                      </td>

                      <td
                        style={{
                          padding: "10px 8px",
                          borderBottom: "1px solid #172033",
                          color:
                            row.billing_state === "BILLABLE"
                              ? "#22c55e"
                              : row.billing_state === "NOT_CONFIGURED"
                                ? "#f59e0b"
                                : "#94a3b8",
                          whiteSpace: "nowrap"
                        }}
                      >
                        {stateLabel}
                      </td>

                      <td
                        style={{
                          padding: "10px 8px",
                          borderBottom: "1px solid #172033",
                          color: "#e2e8f0",
                          whiteSpace: "nowrap"
                        }}
                      >
                        {row.total_mrr === null
                          ? "—"
                          : `${symbol}${Number(
                              row.total_mrr || 0
                            ).toLocaleString("tr-TR")}`}
                      </td>

                      <td
                        style={{
                          padding: "10px 8px",
                          borderBottom: "1px solid #172033"
                        }}
                      >
                        {Number(row.tenant_id) !== 1 && (
                          <div
                            style={{
                              display: "flex",
                              gap: 6,
                              flexWrap: "wrap",
                              alignItems: "center",
                              minWidth: 250
                            }}
                          >
                            <button
                              style={{
                                ...S.btnSm("#334155"),
                                whiteSpace: "nowrap"
                              }}
                              onClick={() =>
                                openBillableMachineModal(row)
                              }
                            >
                              Makine Adedi
                            </button>

                            <button
                              style={{
                                ...S.btnSm("#1d4ed8"),
                                whiteSpace: "nowrap"
                              }}
                              onClick={() =>
                                openTenantMachinePriceModal(row)
                              }
                            >
                              Özel Fiyat
                            </button>

                            <button
                              style={{
                                ...S.btnSm("#6d28d9"),
                                whiteSpace: "nowrap"
                              }}
                              onClick={() =>
                                openModuleAddonModal(row)
                              }
                            >
                              {sa.moduleAddonManage}
                            </button>
                          </div>
                        )}
                      </td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
          </div>
        </div>

<div
              style={{
                display: "grid",
                gridTemplateColumns:
                  "repeat(auto-fit, minmax(220px, 1fr))",
                gap: 14
              }}
            >
              {machinePriceCatalog.map(plan => {
                const label =
                  PLAN_LABELS[plan.plan_code] ||
                  plan.plan_name ||
                  plan.plan_code;

                return (
                  <div
                    key={plan.plan_code}
                    style={{
                      padding: 16,
                      borderRadius: 12,
                      border: "1px solid #334155",
                      background: "#0f172a"
                    }}
                  >
                    <div
                      style={{
                        display: "flex",
                        alignItems: "center",
                        justifyContent: "space-between",
                        gap: 10,
                        marginBottom: 14
                      }}
                    >
                      <div
                        style={{
                          fontSize: 15,
                          fontWeight: 700,
                          color:
                            PLAN_COLORS[plan.plan_code] ||
                            "#f1f5f9"
                        }}
                      >
                        {label}
                      </div>

                      <div
                        style={{
                          fontSize: 11,
                          color: "#94a3b8"
                        }}
                      >
                        {sa.perMachineMonthly}
                      </div>
                    </div>

                    {["TRY", "USD", "EUR"].map(currency => (
                      <div
                        key={currency}
                        style={{
                          display: "flex",
                          justifyContent: "space-between",
                          alignItems: "center",
                          gap: 12,
                          padding: "7px 0",
                          borderBottom:
                            "1px solid rgba(148,163,184,.12)"
                        }}
                      >
                        <span
                          style={{
                            color: "#94a3b8",
                            fontSize: 12,
                            fontWeight: 600
                          }}
                        >
                          {currency}
                        </span>

                        <span
                          style={{
                            color: plan?.is_configured?.[currency]
                              ? "#f8fafc"
                              : "#64748b",
                            fontWeight: 700
                          }}
                        >
                          {plan?.is_configured?.[currency]
                            ? Number(
                                plan?.prices?.[currency] || 0
                              ).toLocaleString(
                                getLocale(),
                                {
                                  minimumFractionDigits: 2,
                                  maximumFractionDigits: 2
                                }
                              )
                            : sa.machinePriceNotConfigured}
                        </span>
                      </div>
                    ))}

                    <button
                      style={{
                        ...S.btnSm("#334155"),
                        width: "100%",
                        marginTop: 14
                      }}
                      onClick={() => openMachinePriceModal(plan)}
                    >
                      {sa.machinePriceEdit}
                    </button>
                  </div>
                );
              })}
            </div>
              </>
          )}
        </div>
      )}

      </main>
      </div>

      {/* ERP Connector Modal */}
      {modal === "machinePrice" && (
        <div
          style={S.overlay}
          onClick={e =>
            e.target === e.currentTarget && setModal(null)
          }
        >
          <div style={S.modalBox}>
            <div
              style={{
                fontSize: 16,
                fontWeight: 700,
                color: "#f1f5f9",
                marginBottom: 6
              }}
            >
              {sa.machinePriceModalTitle}
            </div>

            <div
              style={{
                color: "#94a3b8",
                fontSize: 13,
                marginBottom: 20
              }}
            >
              {form.plan_name || form.plan_code} ·{" "}
              {sa.perMachineMonthly}
            </div>

            <div style={S.row}>
              {["TRY", "USD", "EUR"].map(currency => (
                <div key={currency}>
                  <label style={S.label}>
                    {currency} · {sa.machinePriceMonthly}
                  </label>

                  <input
                    style={S.input}
                    type="number"
                    min="0"
                    step="0.01"
                    value={form?.prices?.[currency] ?? ""}
                    onChange={e =>
                      setForm({
                        ...form,
                        prices: {
                          ...(form.prices || {}),
                          [currency]: e.target.value
                        }
                      })
                    }
                  />
                </div>
              ))}
            </div>

            <div
              style={{
                marginTop: 12,
                padding: 12,
                borderRadius: 8,
                background: "#0f172a",
                border: "1px solid #334155",
                color: "#94a3b8",
                fontSize: 12,
                lineHeight: 1.6
              }}
            >
              {sa.machinePriceHelp}
            </div>

            <div
              style={{
                display: "flex",
                justifyContent: "flex-end",
                gap: 10,
                marginTop: 22
              }}
            >
              <button
                style={S.btnSm("#334155")}
                onClick={() => setModal(null)}
              >
                {sa.cancel}
              </button>

              <button
                style={S.btnSm("#5b21b6")}
                onClick={saveMachinePrice}
              >
                {sa.machinePriceSave}
              </button>
            </div>
          </div>
        </div>
      )}

      {modal === "erpConnector" && (
        <div
          style={S.overlay}
          onClick={(e) => e.target === e.currentTarget && setModal(null)}
        >
          <div style={S.modalBox}>
            <div
              style={{
                fontSize: 16,
                fontWeight: 800,
                color: "#f1f5f9",
                marginBottom: 6
              }}
            >
              {sa.erpConnectorTitle}
            </div>

            <div
              style={{
                fontSize: 12,
                color: "#64748b",
                marginBottom: 20
              }}
            >
              {form.tenant_name || `Tenant #${form.tenant_id}`}
            </div>

            <div
              style={{
                padding: 14,
                background: "#0a0f1a",
                border: "1px solid #1e293b",
                borderRadius: 12,
                marginBottom: 18
              }}
            >
              <label
                style={{
                  ...S.label,
                  display: "flex",
                  alignItems: "center",
                  gap: 10,
                  cursor: "pointer",
                  marginBottom: 0
                }}
              >
                <input
                  type="checkbox"
                  checked={!!form.enabled}
                  onChange={e =>
                    setForm({
                      ...form,
                      enabled: e.target.checked
                    })
                  }
                />

                <span>
                  {form.enabled
                    ? sa.erpConnectorEnabled
                    : sa.erpConnectorDisabled}
                </span>
              </label>
            </div>

            <div style={S.row}>
              <div>
                <label style={S.label}>
                  {sa.erpConnectorGrantType}
                </label>

                <select
                  style={S.input}
                  value={form.grant_type || "addon"}
                  onChange={e =>
                    setForm({
                      ...form,
                      grant_type: e.target.value
                    })
                  }
                >
                  <option value="addon">
                    {sa.erpConnectorGrantAddon}
                  </option>
                  <option value="gift">
                    {sa.erpConnectorGrantGift}
                  </option>
                  <option value="trial">
                    {sa.erpConnectorGrantTrial}
                  </option>
                  <option value="manual">
                    {sa.erpConnectorGrantManual}
                  </option>
                </select>
              </div>

              <div>
                <label style={S.label}>
                  {sa.erpConnectorCurrency}
                </label>

                <select
                  style={S.input}
                  value={form.currency || "TRY"}
                  onChange={e =>
                    setForm({
                      ...form,
                      currency: e.target.value
                    })
                  }
                >
                  <option value="TRY">TRY</option>
                  <option value="USD">USD</option>
                  <option value="EUR">EUR</option>
                </select>
              </div>
            </div>

            <div style={S.row}>
              <div>
                <label style={S.label}>
                  {sa.erpConnectorMonthlyPrice}
                </label>

                <input
                  style={S.input}
                  type="number"
                  min="0"
                  step="0.01"
                  value={form.monthly_price ?? 0}
                  onChange={e =>
                    setForm({
                      ...form,
                      monthly_price: e.target.value
                    })
                  }
                />
              </div>

              <div>
                <label style={S.label}>
                  {sa.erpConnectorYearlyPrice}
                </label>

                <input
                  style={S.input}
                  type="number"
                  min="0"
                  step="0.01"
                  value={form.yearly_price ?? 0}
                  onChange={e =>
                    setForm({
                      ...form,
                      yearly_price: e.target.value
                    })
                  }
                />
              </div>
            </div>

            <div style={S.row}>
              <div>
                <label style={S.label}>
                  {sa.erpConnectorStartsAt}
                </label>

                <input
                  style={S.input}
                  type="date"
                  value={form.starts_at || ""}
                  onChange={e =>
                    setForm({
                      ...form,
                      starts_at: e.target.value
                    })
                  }
                />
              </div>

              <div>
                <label style={S.label}>
                  {sa.erpConnectorExpiresAt}
                </label>

                <input
                  style={S.input}
                  type="date"
                  value={form.expires_at || ""}
                  onChange={e =>
                    setForm({
                      ...form,
                      expires_at: e.target.value
                    })
                  }
                />
              </div>
            </div>

            <div>
              <label style={S.label}>
                {sa.erpConnectorNotes}
              </label>

              <textarea
                style={{
                  ...S.input,
                  minHeight: 90,
                  resize: "vertical"
                }}
                maxLength={500}
                value={form.notes || ""}
                onChange={e =>
                  setForm({
                    ...form,
                    notes: e.target.value
                  })
                }
                placeholder={sa.erpConnectorNotesPlaceholder}
              />
            </div>

            <div
              style={{
                display: "flex",
                justifyContent: "flex-end",
                gap: 10,
                marginTop: 22
              }}
            >
              <button
                style={S.btnSm("#334155")}
                onClick={() => setModal(null)}
              >
                {sa.cancel}
              </button>

              <button
                style={S.btnSm("#5b21b6")}
                onClick={saveErpConnector}
              >
                {sa.erpConnectorSave}
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Firma Modal */}
      {modal === "tenant" && (
        <div style={S.overlay} onClick={(e) => e.target === e.currentTarget && setModal(null)}>
          <div style={S.modalBox}>
            <div style={{ fontSize: 16, fontWeight: 700, color: "#f1f5f9", marginBottom: 20 }}>
              {form.id ? sa.editCompany : sa.addCompany}
            </div>
            <div style={S.row}>
              <div>
                <label style={S.label}>{sa.companyName} *</label>
                <input style={S.input} value={form.name || ""} onChange={e => setForm({ ...form, name: e.target.value })} placeholder="Komark Makine" />
              </div>
              <div>
                <label style={S.label}>{sa.subdomain} *</label>
                <input style={S.input} value={form.subdomain || ""} onChange={e => setForm({ ...form, subdomain: e.target.value })} placeholder="komark" />
              </div>
            </div>
            <div style={S.row}>
              <div>
                <label style={S.label}>{sa.email}</label>
                <input style={S.input} value={form.contact_email || ""} onChange={e => setForm({ ...form, contact_email: e.target.value })} placeholder="info@komark.com" />
              </div>
              <div>
                <label style={S.label}>{sa.phone}</label>
                <input style={S.input} value={form.contact_phone || ""} onChange={e => setForm({ ...form, contact_phone: e.target.value })} placeholder="+90 532..." />
              </div>
            </div>
            <div style={S.row}>
              <div>
                <label style={S.label}>{sa.plan}</label>
                <select style={S.input} value={form.plan || "trial"} onChange={e => setForm({ ...form, plan: e.target.value })}>
                  {(plans.length ? plans : [
                    { code: "trial", name: "Deneme" },
                    { code: "essentials", name: "Essentials" },
                    { code: "professional", name: "Professional" },
                    { code: "advanced", name: "Advanced" },
                    { code: "enterprise", name: "Enterprise" }
                  ]).map(p => (
                    <option key={p.code} value={p.code}>{p.code === "trial" ? sa.planTrial : (PLAN_LABELS[p.code] || p.name || p.code)}</option>
                  ))}
                </select>
              </div>
              <div>
                <label style={S.label}>{sa.endDate}</label>
                <input style={S.input} type="date" value={form.paid_until ? form.paid_until.split("T")[0] : ""} onChange={e => setForm({ ...form, paid_until: e.target.value })} />
              </div>
            </div>
            <div style={S.row}>
              <div>
                <label style={S.label}>{sa.dataSource}</label>
                <select style={S.input} value={form.data_source || "simulation"} onChange={e => setForm({ ...form, data_source: e.target.value })}>
                  <option value="simulation">{sa.simulationOption}</option>
                  <option value="real">{sa.realMachineOption}</option>
                </select>

            {!form.id && (
              <>
                <label style={S.label}>
                  Lisanslı Makine Adedi
                </label>
                <input
                  type="number"
                  min="0"
                  max="99999"
                  step="1"
                  style={S.input}
                  value={form.licensed_machine_count || ""}
                  onChange={(e) =>
                    setForm({
                      ...form,
                      licensed_machine_count: e.target.value
                    })
                  }
                />
              </>
            )}
              </div>

              <div style={{ display: "flex", gap: 16, alignItems: "center", paddingTop: 20 }}>
                <label style={{ ...S.label, display: "flex", alignItems: "center", gap: 8, cursor: "pointer" }}>
                  <input type="checkbox" checked={!!form.is_demo} onChange={e => setForm({ ...form, is_demo: e.target.checked ? 1 : 0 })} />
                  Demo hesap
                </label>
                <label style={{ ...S.label, display: "flex", alignItems: "center", gap: 8, cursor: "pointer" }}>
                  <input type="checkbox" checked={!!form.is_active} onChange={e => setForm({ ...form, is_active: e.target.checked ? 1 : 0 })} />
                  Aktif
                </label>
              </div>
            </div>
            <div style={{ display: "flex", gap: 10, justifyContent: "flex-end", marginTop: 8 }}>
              <button style={S.btn("#1e3a5f")} onClick={() => setModal(null)}>{sa.cancel}</button>
              <button style={S.btn()} onClick={saveTenant}>{sa.save}</button>
            </div>
          </div>
        </div>
      )}


      {/* Lisans Modal */}
      {modal === "license" && (
        <div style={S.overlay} onClick={(e) => e.target === e.currentTarget && setModal(null)}>
          <div style={S.modalBox}>
            <div style={{ fontSize: 16, fontWeight: 700, color: "#f1f5f9", marginBottom: 20 }}>
              {sa.editLicense}
            </div>

            <label style={S.label}>{sa.plan}</label>
            <select
              style={S.input}
              value={form.plan_code || "trial"}
              onChange={(e) => setForm({ ...form, plan_code: e.target.value })}
            >
              {plans.map((p) => (
                <option key={p.code} value={p.code}>
                  {p.name || p.code}
                </option>
              ))}
            </select>

            <label style={S.label}>{sa.currency}</label>
            <select
              style={S.input}
              value={form.currency || "TRY"}
              onChange={(e) => setForm({ ...form, currency: e.target.value })}
            >
              <option value="TRY">TRY</option>
              <option value="USD">USD</option>
              <option value="EUR">EUR</option>
            </select>

            <label style={S.label}>{sa.status}</label>
            <select
              style={S.input}
              value={form.status || "active"}
              onChange={(e) => setForm({ ...form, status: e.target.value })}
            >
              <option value="active">{sa.statusActive}</option>
              <option value="trial">{sa.statusTrial}</option>
              <option value="expired">{sa.statusExpired}</option>
              <option value="cancelled">{sa.statusCancelled}</option>
            </select>

            <label style={S.label}>{sa.endDate}</label>
            <input
              type="date"
              style={S.input}
              value={form.paid_until || ""}
              onChange={(e) => setForm({ ...form, paid_until: e.target.value })}
            />

            <label style={S.label}>{sa.trialEndDate}</label>
            <input
              type="date"
              style={S.input}
              value={form.trial_ends_at || ""}
              onChange={(e) => setForm({ ...form, trial_ends_at: e.target.value })}
            />

            <div style={{ display: "flex", gap: 10, justifyContent: "flex-end", marginTop: 8 }}>
              <button style={S.btn("#1e3a5f")} onClick={() => setModal(null)}>{sa.cancel}</button>
              <button style={S.btn()} onClick={saveSubscription}>{sa.save}</button>
            </div>
          </div>
        </div>
      )}


      {moduleAddonModal && (
        <div
          style={S.overlay}
          onClick={(e) => {
            if (e.target === e.currentTarget) {
              setModuleAddonModal(null);
              setModuleAddonData(null);
              resetModuleAddonForm();
            }
          }}
        >
          <div
            style={{
              ...S.modalBox,
              width: 760,
              maxWidth: "94vw",
              maxHeight: "90vh",
              overflowY: "auto"
            }}
          >
            <div
              style={{
                display: "flex",
                justifyContent: "space-between",
                alignItems: "flex-start",
                gap: 16,
                marginBottom: 20
              }}
            >
              <div>
                <div
                  style={{
                    fontSize: 18,
                    fontWeight: 800,
                    color: "#f8fafc"
                  }}
                >
                  {sa.moduleAddonTitle}
                </div>

                <div
                  style={{
                    marginTop: 5,
                    fontSize: 12,
                    color: "#64748b",
                    lineHeight: 1.5
                  }}
                >
                  {moduleAddonModal.name || "—"}
                  {" · "}
                  {sa.moduleAddonSubtitle}
                </div>
              </div>

              <button
                type="button"
                style={S.btnSm("#334155")}
                onClick={() => {
                  setModuleAddonModal(null);
                  setModuleAddonData(null);
                  resetModuleAddonForm();
                }}
              >
                ✕
              </button>
            </div>

            <div
              style={{
                padding: 16,
                border: "1px solid #1e3a5f",
                borderRadius: 12,
                background: "#0a0f1a",
                marginBottom: 18
              }}
            >
              <div
                style={{
                  fontSize: 13,
                  fontWeight: 800,
                  color: "#e2e8f0",
                  marginBottom: 12
                }}
              >
                {sa.moduleAddonCurrent}
              </div>

              {moduleAddonLoading ? (
                <div
                  style={{
                    fontSize: 12,
                    color: "#94a3b8"
                  }}
                >
                  {sa.loading}
                </div>
              ) : (moduleAddonData?.overrides || []).length === 0 ? (
                <div
                  style={{
                    fontSize: 12,
                    color: "#64748b"
                  }}
                >
                  {sa.moduleAddonNone}
                </div>
              ) : (
                <div
                  style={{
                    display: "grid",
                    gap: 8
                  }}
                >
                  {(moduleAddonData?.overrides || []).map(item => (
                    <div
                      key={item.module_key}
                      style={{
                        display: "flex",
                        justifyContent: "space-between",
                        alignItems: "center",
                        gap: 12,
                        padding: "10px 12px",
                        borderRadius: 9,
                        border: "1px solid #1e293b",
                        background: "#0f172a"
                      }}
                    >
                      <div style={{ minWidth: 0 }}>
                        <div
                          style={{
                            display: "flex",
                            gap: 7,
                            alignItems: "center",
                            flexWrap: "wrap"
                          }}
                        >
                          <strong
                            style={{
                              color: "#f8fafc",
                              fontSize: 13
                            }}
                          >
                            {item.module_key}
                          </strong>

                          <span
                            style={{
                              padding: "2px 7px",
                              borderRadius: 20,
                              fontSize: 10,
                              fontWeight: 800,
                              background:
                                Number(item.enabled || 0) === 1
                                  ? "#14532d"
                                  : "#7f1d1d",
                              color:
                                Number(item.enabled || 0) === 1
                                  ? "#86efac"
                                  : "#fca5a5"
                            }}
                          >
                            {Number(item.enabled || 0) === 1
                              ? sa.moduleAddonEnabled
                              : sa.erpConnectorDisabled}
                          </span>

                          <span
                            style={{
                              padding: "2px 7px",
                              borderRadius: 20,
                              fontSize: 10,
                              fontWeight: 800,
                              background: "#312e81",
                              color: "#c4b5fd"
                            }}
                          >
                            {item.grant_type}
                          </span>
                        </div>

                        <div
                          style={{
                            marginTop: 5,
                            fontSize: 11,
                            color: "#64748b"
                          }}
                        >
                          {item.grant_type === "addon"
                            ? `${Number(
                                item.monthly_price || 0
                              ).toLocaleString(getLocale())} ${
                                item.currency || "TRY"
                              } / ${sa.erpConnectorMonthly}`
                            : `${Number(
                                item.monthly_price || 0
                              ).toLocaleString(getLocale())} ${
                                item.currency || "TRY"
                              }`}
                        </div>
                      </div>

                      <div
                        style={{
                          display: "flex",
                          gap: 6,
                          flexShrink: 0
                        }}
                      >
                        <button
                          type="button"
                          style={S.btnSm("#1d4ed8")}
                          disabled={moduleAddonSaving}
                          onClick={() => editModuleAddon(item)}
                        >
                          {sa.moduleAddonEdit}
                        </button>

                        <button
                          type="button"
                          style={S.btnSm("#7f1d1d")}
                          disabled={moduleAddonSaving}
                          onClick={() =>
                            deleteModuleAddon(item.module_key)
                          }
                        >
                          {sa.moduleAddonRemove}
                        </button>
                      </div>
                    </div>
                  ))}
                </div>
              )}
            </div>

            <div
              style={{
                fontSize: 13,
                fontWeight: 800,
                color: "#e2e8f0",
                marginBottom: 12
              }}
            >
              {sa.moduleAddonSave}
            </div>

            <label style={S.label}>
              {sa.moduleAddonModule}
            </label>

            <select
              style={S.input}
              value={moduleAddonForm.module_key}
              onChange={(e) =>
                setModuleAddonForm(prev => ({
                  ...prev,
                  module_key: e.target.value
                }))
              }
            >
              <option value="">
                {sa.moduleAddonModule}...
              </option>

              {(moduleAddonData?.available_modules || []).map(
                moduleKey => (
                  <option key={moduleKey} value={moduleKey}>
                    {moduleKey}
                  </option>
                )
              )}
            </select>

            <div style={S.row}>
              <div>
                <label style={S.label}>
                  {sa.moduleAddonGrantType}
                </label>

                <select
                  style={S.input}
                  value={moduleAddonForm.grant_type}
                  onChange={(e) =>
                    setModuleAddonForm(prev => ({
                      ...prev,
                      grant_type: e.target.value,
                      monthly_price:
                        e.target.value === "addon"
                          ? prev.monthly_price
                          : "0",
                      yearly_price:
                        e.target.value === "addon"
                          ? prev.yearly_price
                          : "0"
                    }))
                  }
                >
                  <option value="addon">
                    {sa.moduleAddonGrantAddon}
                  </option>
                  <option value="gift">
                    {sa.moduleAddonGrantGift}
                  </option>
                  <option value="trial">
                    {sa.moduleAddonGrantTrial}
                  </option>
                  <option value="manual">
                    {sa.moduleAddonGrantManual}
                  </option>
                </select>
              </div>

              <div>
                <label style={S.label}>
                  {sa.moduleAddonCurrency}
                </label>

                <select
                  style={S.input}
                  value={moduleAddonForm.currency}
                  onChange={(e) =>
                    setModuleAddonForm(prev => ({
                      ...prev,
                      currency: e.target.value
                    }))
                  }
                >
                  <option value="TRY">TRY</option>
                  <option value="USD">USD</option>
                  <option value="EUR">EUR</option>
                </select>
              </div>
            </div>

            <div style={S.row}>
              <div>
                <label style={S.label}>
                  {sa.moduleAddonMonthlyPrice}
                </label>

                <input
                  style={S.input}
                  type="number"
                  min="0"
                  step="0.01"
                  disabled={moduleAddonForm.grant_type !== "addon"}
                  value={moduleAddonForm.monthly_price}
                  onChange={(e) =>
                    setModuleAddonForm(prev => ({
                      ...prev,
                      monthly_price: e.target.value
                    }))
                  }
                />
              </div>

              <div>
                <label style={S.label}>
                  {sa.moduleAddonYearlyPrice}
                </label>

                <input
                  style={S.input}
                  type="number"
                  min="0"
                  step="0.01"
                  disabled={moduleAddonForm.grant_type !== "addon"}
                  value={moduleAddonForm.yearly_price}
                  onChange={(e) =>
                    setModuleAddonForm(prev => ({
                      ...prev,
                      yearly_price: e.target.value
                    }))
                  }
                />
              </div>
            </div>

            <div style={S.row}>
              <div>
                <label style={S.label}>
                  {sa.moduleAddonStartsAt}
                </label>

                <input
                  style={S.input}
                  type="date"
                  value={moduleAddonForm.starts_at}
                  onChange={(e) =>
                    setModuleAddonForm(prev => ({
                      ...prev,
                      starts_at: e.target.value
                    }))
                  }
                />
              </div>

              <div>
                <label style={S.label}>
                  {sa.moduleAddonExpiresAt}
                </label>

                <input
                  style={S.input}
                  type="date"
                  value={moduleAddonForm.expires_at}
                  onChange={(e) =>
                    setModuleAddonForm(prev => ({
                      ...prev,
                      expires_at: e.target.value
                    }))
                  }
                />
              </div>
            </div>

            <label
              style={{
                ...S.label,
                display: "flex",
                alignItems: "center",
                gap: 8,
                marginBottom: 12,
                color: "#cbd5e1"
              }}
            >
              <input
                type="checkbox"
                checked={Boolean(moduleAddonForm.enabled)}
                onChange={(e) =>
                  setModuleAddonForm(prev => ({
                    ...prev,
                    enabled: e.target.checked
                  }))
                }
              />
              {sa.moduleAddonEnabled}
            </label>

            <label style={S.label}>
              {sa.moduleAddonNotes}
            </label>

            <textarea
              style={{
                ...S.input,
                minHeight: 78,
                resize: "vertical"
              }}
              maxLength={500}
              placeholder={sa.moduleAddonNotesPlaceholder}
              value={moduleAddonForm.notes}
              onChange={(e) =>
                setModuleAddonForm(prev => ({
                  ...prev,
                  notes: e.target.value
                }))
              }
            />

            <div
              style={{
                display: "flex",
                justifyContent: "flex-end",
                gap: 8,
                marginTop: 8
              }}
            >
              <button
                type="button"
                style={S.btn("#334155")}
                disabled={moduleAddonSaving}
                onClick={() => {
                  setModuleAddonModal(null);
                  setModuleAddonData(null);
                  resetModuleAddonForm();
                }}
              >
                {sa.cancel}
              </button>

              <button
                type="button"
                style={S.btn("#6d28d9")}
                disabled={
                  moduleAddonSaving ||
                  moduleAddonLoading ||
                  !moduleAddonForm.module_key
                }
                onClick={saveModuleAddon}
              >
                {moduleAddonSaving
                  ? sa.loading
                  : sa.moduleAddonSave}
              </button>
            </div>
          </div>
        </div>
      )}

      {tenantMachinePriceModal && tenantMachinePriceData && (
        <div
          style={S.overlay}
          onClick={(e) =>
            e.target === e.currentTarget &&
            !tenantMachinePriceSaving &&
            setTenantMachinePriceModal(null)
          }
        >
          <div style={S.modalBox}>
            <div
              style={{
                fontSize: 16,
                fontWeight: 700,
                color: "#f1f5f9",
                marginBottom: 6
              }}
            >
              Müşteri Özel Makine Fiyatı
            </div>

            <div
              style={{
                fontSize: 13,
                color: "#94a3b8",
                marginBottom: 18
              }}
            >
              {tenantMachinePriceModal.name}
            </div>

            <div
              style={{
                display: "grid",
                gridTemplateColumns: "1fr 1fr",
                gap: 12,
                marginBottom: 14
              }}
            >
              <div>
                <label style={S.label}>Paket</label>
                <input
                  style={S.input}
                  disabled
                  value={
                    PLAN_LABELS[tenantMachinePriceData.plan_code] ||
                    tenantMachinePriceData.plan_code ||
                    "—"
                  }
                />
              </div>

              <div>
                <label style={S.label}>Para Birimi</label>
                <input
                  style={S.input}
                  disabled
                  value={tenantMachinePriceData.currency || "TRY"}
                />
              </div>

              <div>
                <label style={S.label}>Paket Fiyatı</label>
                <input
                  style={S.input}
                  disabled
                  value={
                    tenantMachinePriceData.catalog_price === null ||
                    tenantMachinePriceData.catalog_price === undefined
                      ? "Tanımlanmadı"
                      : tenantMachinePriceData.catalog_price
                  }
                />
              </div>

              <div>
                <label style={S.label}>Aktif Kaynak</label>
                <input
                  style={S.input}
                  disabled
                  value={
                    tenantMachinePriceData.machine_price_source === "TENANT"
                      ? "Müşteri Özel"
                      : tenantMachinePriceData.machine_price_source === "CATALOG"
                        ? "Paket"
                        : "Tanımlanmadı"
                  }
                />
              </div>
            </div>

            <label style={S.label}>
              Aylık Makine Birim Fiyatı
            </label>

            <input
              type="number"
              min="0"
              step="0.01"
              style={S.input}
              value={tenantMachinePriceValue}
              disabled={tenantMachinePriceSaving}
              onChange={(e) =>
                setTenantMachinePriceValue(e.target.value)
              }
            />

            <div
              style={{
                fontSize: 11,
                color: "#94a3b8",
                marginTop: -6,
                marginBottom: 18
              }}
            >
              Kaydettiğinizde bu müşterinin paket makine fiyatının
              yerine özel sözleşme fiyatı kullanılır. Özel fiyat
              kaldırılırsa sistem otomatik olarak paket fiyatına döner.
            </div>

            <div
              style={{
                display: "flex",
                gap: 10,
                justifyContent: "space-between"
              }}
            >
              <div>
                {tenantMachinePriceData.configured && (
                  <button
                    style={S.btn("#7f1d1d")}
                    disabled={tenantMachinePriceSaving}
                    onClick={deleteTenantMachinePrice}
                  >
                    Özel Fiyatı Kaldır
                  </button>
                )}
              </div>

              <div
                style={{
                  display: "flex",
                  gap: 10
                }}
              >
                <button
                  style={S.btn("#1e3a5f")}
                  disabled={tenantMachinePriceSaving}
                  onClick={() =>
                    setTenantMachinePriceModal(null)
                  }
                >
                  İptal
                </button>

                <button
                  style={S.btn()}
                  disabled={tenantMachinePriceSaving}
                  onClick={saveTenantMachinePrice}
                >
                  {tenantMachinePriceSaving
                    ? "Kaydediliyor..."
                    : "Kaydet"}
                </button>
              </div>
            </div>
          </div>
        </div>
      )}

      {billableMachineModal && (
        <div
          style={S.overlay}
          onClick={(e) =>
            e.target === e.currentTarget &&
            !billableMachineSaving &&
            setBillableMachineModal(null)
          }
        >
          <div style={S.modalBox}>
            <div
              style={{
                fontSize: 16,
                fontWeight: 700,
                color: "#f1f5f9",
                marginBottom: 6
              }}
            >
              Faturalandırılan Makine Adedi
            </div>

            <div
              style={{
                fontSize: 13,
                color: "#94a3b8",
                marginBottom: 18
              }}
            >
              {billableMachineModal.name}
            </div>

            <div
              style={{
                display: "grid",
                gridTemplateColumns: "1fr 1fr",
                gap: 12,
                marginBottom: 14
              }}
            >
              <div>
                <label style={S.label}>Gerçek Makine</label>
                <input
                  style={S.input}
                  disabled
                  value={
                    billableMachineModal.actual_machine_count || 0
                  }
                />
              </div>

              <div>
                <label style={S.label}>Mevcut Lisans</label>
                <input
                  style={S.input}
                  disabled
                  value={
                    billableMachineModal.licensed_machine_count === null
                      ? "Tanımlanmadı"
                      : billableMachineModal.licensed_machine_count
                  }
                />
              </div>
            </div>

            <label style={S.label}>
              Yeni Faturalandırılan Makine Adedi
            </label>

            <input
              type="number"
              min={Number(
                billableMachineModal.actual_machine_count || 0
              )}
              max="99999"
              step="1"
              style={S.input}
              value={billableMachineCount}
              onChange={(e) =>
                setBillableMachineCount(e.target.value)
              }
            />

            <div
              style={{
                fontSize: 11,
                color: "#94a3b8",
                marginTop: -6,
                marginBottom: 18
              }}
            >
              Minimum:{" "}
              {Number(
                billableMachineModal.actual_machine_count || 0
              )}. Bu değer makine ekleme limitini ve paket MRR
              hesabını birlikte etkiler.
            </div>

            <div
              style={{
                display: "flex",
                gap: 10,
                justifyContent: "flex-end"
              }}
            >
              <button
                style={S.btn("#1e3a5f")}
                disabled={billableMachineSaving}
                onClick={() =>
                  setBillableMachineModal(null)
                }
              >
                İptal
              </button>

              <button
                style={S.btn()}
                disabled={billableMachineSaving}
                onClick={saveBillableMachineCount}
              >
                {billableMachineSaving
                  ? "Kaydediliyor..."
                  : "Kaydet"}
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Kullanıcı Modal */}
      {modal === "user" && (
        <div style={S.overlay} onClick={(e) => e.target === e.currentTarget && setModal(null)}>
          <div style={S.modalBox}>
            <div style={{ fontSize: 16, fontWeight: 700, color: "#f1f5f9", marginBottom: 20 }}>{form.id ? sa.editUser : sa.addUser}</div>
            <label style={S.label}>{sa.username} *</label>
            <input style={S.input} value={form.username || ""} onChange={e => setForm({ ...form, username: e.target.value })} placeholder="komark_admin" />
            {!form.id && (
              <>
                <label style={S.label}>{sa.password} *</label>
                <input style={S.input} type="password" value={form.password || ""} onChange={e => setForm({ ...form, password: e.target.value })} placeholder="••••••••" />
              </>
            )}
            <div style={S.row}>
              <div>
                <label style={S.label}>{sa.role}</label>
                <select style={S.input} value={form.role || "operator"} onChange={e => setForm({ ...form, role: e.target.value })}>
                  <option value="admin">{sa.roleAdmin}</option>
                  <option value="manager">Manager</option>
                  <option value="production_manager">{sa.roleManager}</option>
                  <option value="quality_manager">{sa.roleQuality}</option>
                  <option value="warehouse_chief">Warehouse Chief</option>
                  <option value="maintenance_chief">Maintenance Chief</option>
                  <option value="hr_manager">HR Manager</option>
                  <option value="accountant">Accountant</option>
                  <option value="technician">{sa.roleTechnician}</option>
                  <option value="warehouse">Warehouse</option>
                  <option value="operator">{sa.roleOperator}</option>
                  <option value="purchasing">Purchasing</option>
                  <option value="sales">Sales</option>
                  <option value="dis_ticaret_mudur">Dış Ticaret Müdürü</option>
                  <option value="dis_ticaret_uzman">Dış Ticaret Uzmanı</option>
                  <option value="dis_ticaret_temsilci">Dış Ticaret Temsilcisi</option>
                  <option value="ic_satis_mudur">Yurt İçi Satış Müdürü</option>
                  <option value="ic_satis_uzman">Yurt İçi Satış Uzmanı</option>
                  <option value="ic_satis_temsilci">Yurt İçi Satış Temsilcisi</option>
                </select>
              </div>
              <div>
                <label style={S.label}>{sa.company}</label>
                <select style={S.input} value={form.tenant_id || ""} onChange={e => setForm({ ...form, tenant_id: e.target.value })}>
                  <option value="">{sa.select}</option>
                  {tenants.map(t => <option key={t.id} value={t.id}>{t.name}</option>)}
                </select>
              </div>
            </div>
            {form.id && (
              <label style={{ display: "flex", alignItems: "center", gap: 8, marginBottom: 14, color: "#cbd5e1", fontSize: 13 }}>
                <input
                  type="checkbox"
                  checked={!!form.is_active}
                  onChange={e => setForm({ ...form, is_active: e.target.checked })}
                />
                Kullanıcı aktif
              </label>
            )}

            <div style={{ display: "flex", gap: 10, justifyContent: "flex-end", marginTop: 8 }}>
              <button style={S.btn("#1e3a5f")} onClick={() => setModal(null)}>{sa.cancel}</button>
              <button style={S.btn()} onClick={form.id ? updateUser : saveUser}>{sa.save}</button>
            </div>
          </div>
        </div>

      )}
    </div>
  );
}
